import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import {
  getInvoiceTotal,
  sumPayments,
  getAppliedPaymentsForInvoice,
  deriveInvoiceFinancials,
  deriveInvoiceStatus,
  deriveJobFinancials,
  deriveFinancialAlerts,
} from '../lib/jobFinancials'
import { deriveJobCosting, derivePvaRows, derivePvaSummary } from '../lib/jobCosting'
import { TAX_RATE } from '../lib/estimateFinancials'
import { maybeGenerateOnCompletion } from '../lib/recurringJobs'
import QuickAddClientModal from '../components/QuickAddClientModal'
import RemindersPanel from '../components/RemindersPanel'
import { useIsMobile } from '../components/Layout'

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS    = ['pending', 'active', 'completed', 'cancelled']
const PRIORITY_OPTIONS  = ['low', 'normal', 'urgent']
const PAYMENT_METHODS   = ['cash', 'check', 'card', 'transfer', 'zelle', 'venmo']
const JOB_TYPES         = ['Landscaping', 'Tree Removal', 'Tree Trimming', 'Stump Grinding', 'Pond/Water Feature', 'Maintenance', 'Hardscape', 'Irrigation', 'Cleanup', 'Other']
const COST_CATEGORIES   = ['materials', 'equipment', 'subcontractor', 'dump_fee', 'fuel', 'rental', 'other']
const RECURRENCE_OPTS   = ['weekly', 'biweekly', 'monthly', 'quarterly']

const STATUS_COLOR   = { active: '#16a34a', completed: '#2563eb', pending: '#d97706', cancelled: '#dc2626' }
const PRIORITY_COLOR = { low: '#6b7280', normal: '#2563eb', urgent: '#dc2626' }
const CAT_COLOR      = { materials: '#2563eb', equipment: '#d97706', subcontractor: '#7c3aed', dump_fee: '#6b7280', fuel: '#0369a1', rental: '#0891b2', other: '#6b7280', labor: '#16a34a' }

// ─── Shared utilities ─────────────────────────────────────────────────────────
const fmt$ = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtH = (h) => `${Number(h || 0).toFixed(1)}h`
const cap  = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

function PhoneLink({ phone, style }) {
  if (!phone) return null
  return <a href={`tel:${phone}`} style={{ ...style, textDecoration: 'none' }}>{phone}</a>
}
function MapLink({ address, style, children }) {
  if (!address) return null
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  return <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...style, textDecoration: 'none' }}>{children || address}</a>
}
function ClientLink({ id, name, style }) {
  if (!id) return null
  return <Link to={`/clients/${id}`} style={{ ...style, textDecoration: 'none' }}>{name}</Link>
}

// ─── JobsList ─────────────────────────────────────────────────────────────────
export function JobsList() {
  const { isAdmin, profile } = useAuth()
  const [jobs, setJobs]           = useState([])
  const [filter, setFilter]       = useState('active')
  const [clientFilter, setClientFilter] = useState('')
  const [typeFilter, setTypeFilter]     = useState('')
  const [dateFrom, setDateFrom]   = useState('')
  const [dateTo, setDateTo]       = useState('')
  const [search, setSearch]       = useState('')
  const [clients, setClients]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => { fetchJobs() }, [])
  useEffect(() => {
    supabase.from('clients').select('id, name').order('name').then(({ data }) => setClients(data || []))
  }, [])

  async function fetchJobs() {
    const { data } = await supabase
      .from('jobs')
      .select('*, client:clients(id, name, phone), assignments:job_assignments(employee_id)')
      .order('created_at', { ascending: false })
    let result = data || []
    if (!isAdmin) result = result.filter(j => (j.assignments || []).some(a => a.employee_id === profile?.id))
    setJobs(result)
    setLoading(false)
  }

  const filtered = jobs.filter(j => {
    if (filter !== 'all' && j.status !== filter) return false
    if (clientFilter && j.client_id !== clientFilter) return false
    if (typeFilter && j.job_type !== typeFilter) return false
    if (dateFrom && j.scheduled_date && j.scheduled_date < dateFrom) return false
    if (dateTo && j.scheduled_date && j.scheduled_date > dateTo) return false
    const q = search.toLowerCase()
    if (q && !j.title.toLowerCase().includes(q) && !(j.client?.name || '').toLowerCase().includes(q)) return false
    return true
  })

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = jobs.filter(j => j.status === s).length
    return acc
  }, { all: jobs.length })

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Jobs</h1>
          <p style={s.sub}>{filtered.length} of {jobs.length} jobs</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isAdmin && (
            <button style={s.filterToggleBtn} onClick={() => setShowFilters(v => !v)}>
              ⚙ {showFilters ? 'Hide' : 'Filters'}
            </button>
          )}
          <Link to="/jobs/new" style={s.addBtn}>+ New Job</Link>
        </div>
      </div>

      <input style={s.search} placeholder="🔍  Search jobs or clients..." value={search} onChange={e => setSearch(e.target.value)} />

      {/* Status filter pills */}
      <div style={s.filters}>
        {['active', 'pending', 'all', 'completed', 'cancelled'].map(f => (
          <button key={f} style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }} onClick={() => setFilter(f)}>
            {cap(f)} {counts[f] != null ? <span style={{ opacity: 0.7, fontSize: '11px' }}>({counts[f]})</span> : null}
          </button>
        ))}
      </div>

      {/* Advanced filters */}
      {showFilters && isAdmin && (
        <div style={s.advFilters}>
          <select style={s.advSelect} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={s.advSelect} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="date" style={s.advSelect} value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From date" />
          <input type="date" style={s.advSelect} value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To date" />
          <button style={s.clearFiltersBtn} onClick={() => { setClientFilter(''); setTypeFilter(''); setDateFrom(''); setDateTo('') }}>Clear</button>
        </div>
      )}

      {loading ? <div style={s.loading}>Loading...</div> : (
        <div style={s.list}>
          {filtered.map(job => (
            <Link to={`/jobs/${job.id}`} key={job.id} style={{ ...s.card, ...(job.is_internal ? s.cardInternal : {}) }}>
              <div style={s.cardTop}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {job.is_internal && <span style={s.internalBadge}>🏢 Internal</span>}
                  <span style={s.jobTitle}>{job.title}</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {job.priority && job.priority !== 'normal' && (
                    <span style={{ ...s.priorityBadge, background: PRIORITY_COLOR[job.priority] }}>{job.priority}</span>
                  )}
                  <span style={{ ...s.statusBadge, background: STATUS_COLOR[job.status] || '#6b7280' }}>{job.status}</span>
                </div>
              </div>
              <div style={s.cardBottom}>
                <span style={s.meta}>👥 {job.client?.name || 'No client'}</span>
                {job.job_type && <span style={s.meta}>🔧 {job.job_type}</span>}
                {job.scheduled_date && <span style={s.meta}>📅 {new Date(job.scheduled_date + 'T12:00:00').toLocaleDateString()}</span>}
                {job.contract_amount > 0 && isAdmin && <span style={s.meta}>{fmt$(job.contract_amount)}</span>}
                {job.payment_status && job.payment_status !== 'unpaid' && (
                  <span style={{ ...s.meta, color: job.payment_status === 'paid' ? '#16a34a' : '#d97706' }}>
                    💵 {job.payment_status}
                  </span>
                )}
              </div>
            </Link>
          ))}
          {filtered.length === 0 && <div style={s.empty}>No jobs found.</div>}
        </div>
      )}
    </div>
  )
}

// ─── JobDetail ────────────────────────────────────────────────────────────────
export function JobDetail() {
  const { isAdmin, profile } = useAuth()
  const navigate = useNavigate()
  const { id } = useParams()
  const isMobile = useIsMobile()

  const [job, setJob]               = useState(null)
  const [costs, setCosts]           = useState([])
  const [photos, setPhotos]         = useState([])
  const [timeEntries, setTimeEntries] = useState([])
  const [employees, setEmployees]   = useState([])
  const [clients, setClients]       = useState([])
  const [invoices, setInvoices]     = useState([])
  const [loading, setLoading]       = useState(true)
  const [uploading, setUploading]   = useState(false)
  const [tab, setTab]               = useState('overview')

  // Cost entry state
  const [newCost, setNewCost]       = useState({ category: 'materials', description: '', amount: '', quantity: '', unit_cost: '' })
  const [addingCost, setAddingCost] = useState(false)
  const [costError, setCostError]   = useState('')

  // Note state
  const [note, setNote] = useState('')

  // Edit modal state
  const [showEdit, setShowEdit]     = useState(false)
  const [editForm, setEditForm]     = useState({})
  const [editSaving, setEditSaving] = useState(false)

  // Crew assignment state
  const [assignSaving, setAssignSaving] = useState(false)

  // Change order state
  const [showChangeOrder, setShowChangeOrder] = useState(false)
  const [changeOrder, setChangeOrder] = useState({ description: '', amount: '', notes: '' })
  const [coSaving, setCoSaving]     = useState(false)

  // Payment state
  const [invoicePayments, setInvoicePayments] = useState([])
  const [showRecordPayment, setShowRecordPayment] = useState(null)
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: new Date().toISOString().split('T')[0], method: 'cash', reference: '', notes: '' })
  const [paymentSaving, setPaymentSaving] = useState(false)

  // Payment schedule state
  const [paymentSchedule, setPaymentSchedule] = useState([])
  const [showAddMilestone, setShowAddMilestone] = useState(false)
  const [milestoneForm, setMilestoneForm] = useState({ label: '', amount: '', due_date: '', status: 'pending' })
  const [milestoneSaving, setMilestoneSaving] = useState(false)

  // Apply-to-invoice reconciliation state
  const [showApplyPayment, setShowApplyPayment] = useState(null) // payment object to apply
  const [applyTargetInvoiceId, setApplyTargetInvoiceId] = useState('')
  const [applyingSaving, setApplyingSaving] = useState(false)

  // Tax state
  const [taxEnabled, setTaxEnabled] = useState(false)

  // Add time entry modal state
  const [showAddTimeModal, setShowAddTimeModal] = useState(false)
  const [newTime, setNewTime] = useState({ employee_id: '', clock_in_date: new Date().toISOString().split('T')[0], clock_in_time: '', clock_out_time: '' })
  const [addingTime, setAddingTime] = useState(false)
  const [timeError, setTimeError] = useState('')

  const fileRef = useRef()

  const fetchAll = useCallback(async () => {
    const [jobRes, costsRes, photosRes, timeRes, empRes, assignRes, invoicesRes, paymentsRes, scheduleRes] = await Promise.all([
      supabase.from('jobs').select('*, client:clients(id, name, phone, email, address)').eq('id', id).single(),
      supabase.from('job_costs').select('*').eq('job_id', id).order('created_at'),
      supabase.from('job_photos').select('*').eq('job_id', id).order('created_at'),
      supabase.from('time_entries').select('*, employee:profiles(id, full_name, hourly_rate)').eq('job_id', id).order('clock_in'),
      isAdmin ? supabase.from('profiles').select('id, full_name, hourly_rate, role, active').order('full_name') : Promise.resolve({ data: [] }),
      supabase.from('job_assignments').select('employee_id, employee:profiles(id, full_name)').eq('job_id', id),
      isAdmin ? supabase.from('invoices').select('*').eq('job_id', id).order('created_at') : Promise.resolve({ data: [] }),
      isAdmin ? supabase.from('invoice_payments').select('*, collector:profiles(full_name)').eq('job_id', id).order('payment_date') : Promise.resolve({ data: [] }),
      isAdmin ? supabase.from('job_payment_schedule').select('*').eq('job_id', id).order('sort_order') : Promise.resolve({ data: [] }),
    ])
    if (jobRes.error) { setLoading(false); return }
    const jobData = jobRes.data
    jobData.assignments = assignRes.data || []
    if (jobData?.estimate_id) {
      const { data: estData } = await supabase.from('estimates').select('job_type, suggested_price, description, total_cost, site_visit_id').eq('id', jobData.estimate_id).maybeSingle()
      jobData.estimate = estData || null
      if (estData?.site_visit_id) {
        const { data: svData } = await supabase.from('site_visits').select('id, title').eq('id', estData.site_visit_id).maybeSingle()
        jobData.sourceSiteVisit = svData || null
      }
    }
    if (jobData?.proposal_id) {
      const { data: propData } = await supabase.from('proposals').select('proposal_number, status, approved_at, approval_source, approved_contract_amount, declined_at').eq('id', jobData.proposal_id).maybeSingle()
      jobData.proposal = propData || null
    }
    setJob(jobData)
    setCosts(costsRes.data || [])
    setPhotos(photosRes.data || [])
    setTimeEntries(timeRes.data || [])
    setEmployees(empRes.data || [])
    setInvoices(invoicesRes.data || [])
    setInvoicePayments(paymentsRes.data || [])
    setPaymentSchedule(scheduleRes.data || [])
    setLoading(false)
  }, [id, isAdmin])

  useEffect(() => { fetchAll() }, [fetchAll])
  useEffect(() => {
    supabase.from('clients').select('id, name, address').order('name').then(({ data }) => setClients(data || []))
  }, [])

  // ── Helper-derived financials ─────────────────────────────────────────────
  // jobFin is the single source of truth for all payment/balance calculations.
  // Tax is a display-only layer added on top of effectiveContract.
  const jobFin         = deriveJobFinancials(job, invoices, invoicePayments)
  const financialAlerts = deriveFinancialAlerts(job, jobFin)

  const baseContract     = jobFin.baseContract
  const changeOrderTotal = jobFin.changeOrderTotal
  const subtotal         = jobFin.effectiveContract
  const taxAmount        = taxEnabled ? subtotal * TAX_RATE : 0
  const totalContract    = subtotal + taxAmount          // display value (may include tax)
  const totalCollected   = jobFin.totalCollected         // ALL payments including unapplied
  const remainingBalance = Math.max(0, totalContract - totalCollected)
  const creditBalance    = Math.max(0, totalCollected - totalContract)
  const isOverpaid       = totalCollected > totalContract && totalContract > 0
  // totalInvoiced uses getInvoiceTotal to handle both invoices.total and invoices.amount
  const totalInvoiced    = invoices.reduce((s, i) => s + getInvoiceTotal(i), 0)

  // ── Job costing (labor + costs + profitability) ───────────────────────────
  // deriveJobCosting is passed totalContract (effectiveContract + tax) as actualRevenue.
  // totalContract is the source of truth for profit/margin — not job.contract_amount directly —
  // because jobFinancials.js may include change order totals in effectiveContract.
  const {
    totalHours,
    totalLaborCost,
    totalMaterialCost,
    totalOtherCost,
    totalAllCosts,
    profit,
    margin,
  } = deriveJobCosting(timeEntries, costs, totalContract)

  // Per-invoice financials from helper (replaces collectedPerInvoice)
  const invFin           = jobFin.invoiceFinancials      // { [invoiceId]: { appliedTotal, balance, ... } }

  // ── Status / payment updates ──────────────────────────────────────────────
  async function updateStatus(status) {
    const wasCompleted = job.status === 'completed'
    const { error } = await supabase.from('jobs').update({ status }).eq('id', id)
    if (error) { alert('Status update failed: ' + error.message); return }
    setJob(j => ({ ...j, status }))
    if (status === 'completed') {
      // Invoicing is opt-in only -- no prompt, no auto-draft on completion.
      // The "📄 Invoice" CTA below is the one and only place a final invoice gets
      // created, and only when clicked. (Per John, 2026-07-13: completing a job and
      // getting billed for it are two separate decisions.)
      const { recurrenceError } = await maybeGenerateOnCompletion(supabase, job, status, wasCompleted, { createdBy: profile?.id })
      if (recurrenceError) alert('Job marked complete, but the next recurring visit could not be created: ' + recurrenceError.message)
    }
  }

  // updatePaymentStatus() removed — payment_status is now derived from invoice_payments
  // and written as a legacy cache by savePayment() / deletePayment() via deriveJobPaymentStatus().

  // Manual "📄 Invoice" CTA — the only place a final invoice gets created. Lands on
  // the real invoice document (/invoices/:id) either way, instead of the compact
  // in-tab row -- an invoice is a real document, not a summary card (per John,
  // 2026-07-13).
  async function createOrViewFinalInvoice() {
    if (!job) return
    const { data: existing, error } = await supabase.from('invoices').select('id').eq('job_id', id).eq('invoice_type', 'final').maybeSingle()
    if (error) { alert('Could not check for an existing invoice: ' + error.message); return }
    const invoiceId = existing ? existing.id : await createFinalInvoice()
    if (invoiceId) navigate(`/invoices/${invoiceId}`)
  }

  async function createFinalInvoice() {
    const dueDate = new Date(); dueDate.setDate(dueDate.getDate() + 30)
    const { data, error } = await supabase.from('invoices').insert([{
      job_id: id, client_id: job.client_id,
      invoice_number: `INV-${Date.now().toString().slice(-6)}`,
      amount: baseContract, status: 'draft', invoice_type: 'final',
      due_date: dueDate.toISOString().split('T')[0],
      notes: 'Thank you for your business!'
    }]).select().single()
    if (error) { alert('Failed to create invoice: ' + error.message); return null }
    // No fetchAll() here -- the only caller navigates straight to /invoices/:id right
    // after, which does its own independent fetch. Refetching this whole page's data
    // just to immediately unmount it wasted ~10 queries and raced the unmount.
    return data.id
  }

  // ── Crew assignment ───────────────────────────────────────────────────────
  async function assignEmployee(empId) {
    const already = job.assignments.some(a => a.employee_id === empId)
    if (already) return
    setAssignSaving(true)
    const { error } = await supabase.from('job_assignments').insert([{ job_id: id, employee_id: empId }])
    if (error) { alert('Assign failed: ' + error.message) }
    setAssignSaving(false)
    fetchAll()
  }

  async function removeEmployee(empId) {
    if (!window.confirm('Remove this employee from the job?')) return
    setAssignSaving(true)
    const { error } = await supabase.from('job_assignments').delete().eq('job_id', id).eq('employee_id', empId)
    if (error) { alert('Remove failed: ' + error.message) }
    setAssignSaving(false)
    fetchAll()
  }

  // Active-only for *new* crew assignment -- but the raw `employees` list stays unfiltered so
  // Add Time Entry can still log/backfill hours for a deactivated employee (Clock.js's payroll
  // tools stay unfiltered for the same reason -- correcting history is a legitimate admin action).
  const unassignedEmployees = employees.filter(e => e.active !== false && !job?.assignments?.some(a => a.employee_id === e.id))

  // ── Photos ────────────────────────────────────────────────────────────────
  async function uploadPhoto(e) {
    const files = Array.from(e.target.files)
    if (!files.length) return
    setUploading(true)
    for (const file of files) {
      const ext = file.name.split('.').pop()
      const path = `jobs/${id}/${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('job-photos').upload(path, file)
      if (!upErr) {
        const { data: { publicUrl } } = supabase.storage.from('job-photos').getPublicUrl(path)
        await supabase.from('job_photos').insert([{ job_id: id, url: publicUrl, path, uploaded_by: profile?.id }])
      }
    }
    setUploading(false)
    fetchAll()
  }

  // ── Costs ─────────────────────────────────────────────────────────────────
  async function addCost() {
    if (!newCost.description || !newCost.amount) return
    setAddingCost(true); setCostError('')
    const amt = parseFloat(newCost.amount)
    const qty = newCost.quantity ? parseFloat(newCost.quantity) : null
    const uc  = newCost.unit_cost ? parseFloat(newCost.unit_cost) : null
    const { error } = await supabase.from('job_costs').insert([{
      job_id: id, category: newCost.category,
      description: newCost.description, amount: amt,
      quantity: qty, unit_cost: uc, added_by: profile?.id
    }])
    if (error) { setCostError('Failed: ' + error.message); setAddingCost(false); return }
    setNewCost({ category: 'materials', description: '', amount: '', quantity: '', unit_cost: '' })
    setAddingCost(false)
    fetchAll()
  }

  async function deleteCost(costId) {
    if (!window.confirm('Delete this cost entry?')) return
    await supabase.from('job_costs').delete().eq('id', costId)
    fetchAll()
  }

  // ── Time entry (manual add) ────────────────────────────────────────────────
  async function addTime() {
    if (!newTime.employee_id || !newTime.clock_in_date || !newTime.clock_in_time || !newTime.clock_out_time) return
    setAddingTime(true); setTimeError('')
    const clock_in  = new Date(`${newTime.clock_in_date}T${newTime.clock_in_time}`)
    const clock_out = new Date(`${newTime.clock_in_date}T${newTime.clock_out_time}`)
    if (clock_out <= clock_in) { setTimeError('Clock-out must be after clock-in.'); setAddingTime(false); return }
    const { error } = await supabase.from('time_entries').insert([{
      job_id: id, employee_id: newTime.employee_id,
      clock_in: clock_in.toISOString(), clock_out: clock_out.toISOString(),
    }])
    if (error) { setTimeError('Failed: ' + error.message); setAddingTime(false); return }
    setNewTime(t => ({ ...t, clock_in_time: '', clock_out_time: '' }))
    setShowAddTimeModal(false)
    setAddingTime(false)
    fetchAll()
  }

  // ── Notes ─────────────────────────────────────────────────────────────────
  async function saveNote() {
    if (!note.trim()) return
    const ts = new Date().toLocaleString()
    const updated = job.notes ? `${job.notes}\n\n[${ts}] ${note}` : `[${ts}] ${note}`
    await supabase.from('jobs').update({ notes: updated }).eq('id', id)
    setJob(j => ({ ...j, notes: updated }))
    setNote('')
  }

  // ── Edit modal ────────────────────────────────────────────────────────────
  function openEdit() {
    setEditForm({
      title: job.title || '', client_id: job.client?.id || '',
      job_type: job.job_type || '', status: job.status || 'pending',
      priority: job.priority || 'normal', scheduled_date: job.scheduled_date || '',
      end_date: job.end_date || '', start_time: job.start_time || '',
      address: job.address || '', description: job.description || '',
      contract_amount: job.contract_amount || '', crew_size: job.crew_size || '',
      deposit_amount: job.deposit_amount || '', deposit_received: job.deposit_received || false,
      // payment_status removed from edit form — derived from invoice_payments, not manually set
      payment_method: job.payment_method || '',
      estimated_hours: job.estimated_hours || '', is_recurring: job.is_recurring || false,
      recurrence_frequency: job.recurrence_frequency || '',
      is_internal: job.is_internal || false,
    })
    setShowEdit(true)
  }

  function ef(field, value) { setEditForm(f => ({ ...f, [field]: value })) }

  async function saveEdit() {
    setEditSaving(true)
    const { error } = await supabase.from('jobs').update({
      title: editForm.title, client_id: editForm.client_id || null,
      job_type: editForm.job_type || null, status: editForm.status,
      priority: editForm.priority, scheduled_date: editForm.scheduled_date || null,
      end_date: editForm.end_date || null, start_time: editForm.start_time || null,
      address: editForm.address || null, description: editForm.description || null,
      contract_amount: editForm.contract_amount ? parseFloat(editForm.contract_amount) : null,
      crew_size: editForm.crew_size ? parseInt(editForm.crew_size) : null,
      deposit_amount: editForm.deposit_amount ? parseFloat(editForm.deposit_amount) : null,
      deposit_received: editForm.deposit_received,
      // payment_status intentionally omitted — it is derived from invoice_payments
      payment_method: editForm.payment_method || null,
      estimated_hours: editForm.estimated_hours ? parseFloat(editForm.estimated_hours) : null,
      is_recurring: editForm.is_recurring || false,
      recurrence_frequency: editForm.recurrence_frequency || null,
      is_internal: editForm.is_internal || false,
    }).eq('id', id)
    if (error) { alert('Save failed: ' + error.message); setEditSaving(false); return }
    // Sync unpaid invoices to match updated contract amount
    if (editForm.contract_amount) {
      await supabase.from('invoices')
        .update({ client_id: editForm.client_id || null, amount: parseFloat(editForm.contract_amount) })
        .eq('job_id', id).eq('invoice_type', 'final').neq('status', 'paid')
    }
    // Edit modal is a second place status can become 'completed' besides the
    // status dropdown -- route through the same recurrence hook so this path
    // doesn't silently skip generating the next recurring visit.
    const wasCompleted = job.status === 'completed'
    const savedJob = { ...job, scheduled_date: editForm.scheduled_date || null, is_recurring: editForm.is_recurring, recurrence_frequency: editForm.recurrence_frequency }
    const { recurrenceError } = await maybeGenerateOnCompletion(supabase, savedJob, editForm.status, wasCompleted, { createdBy: profile?.id })
    if (recurrenceError) alert('Saved, but the next recurring visit could not be created: ' + recurrenceError.message)
    setEditSaving(false); setShowEdit(false); fetchAll()
  }

  // ── Change orders ─────────────────────────────────────────────────────────
  async function saveChangeOrder() {
    if (!changeOrder.description || !changeOrder.amount) return
    setCoSaving(true)
    const amt = parseFloat(changeOrder.amount)
    const invoiceNum = `CO-${Date.now().toString().slice(-6)}`
    const dueDate = new Date(); dueDate.setDate(dueDate.getDate() + 14)
    const { error } = await supabase.from('invoices').insert([{
      job_id: id, client_id: job.client_id,
      invoice_number: invoiceNum, amount: amt,
      status: 'draft', invoice_type: 'change_order',
      due_date: dueDate.toISOString().split('T')[0],
      notes: changeOrder.notes || changeOrder.description,
    }])
    if (error) { alert('Change order failed: ' + error.message); setCoSaving(false); return }
    // Also record it as a job cost note
    await supabase.from('job_costs').insert([{
      job_id: id, category: 'other',
      description: `Change Order: ${changeOrder.description}`,
      amount: 0, added_by: profile?.id
    }])
    setChangeOrder({ description: '', amount: '', notes: '' })
    setCoSaving(false); setShowChangeOrder(false); fetchAll()
  }

  // ── Payment ledger ───────────────────────────────────────────────────────

  function openRecordPayment(inv) {
    // Use helper to get accurate remaining balance for this invoice
    const { appliedTotal, invoiceTotal } = deriveInvoiceFinancials(inv, invoicePayments)
    const remaining = invoiceTotal - appliedTotal
    setPaymentForm({
      amount: remaining > 0 ? remaining.toFixed(2) : '',
      payment_date: new Date().toISOString().split('T')[0],
      method: 'cash', reference: '', notes: '',
      invoice_id: inv.id,
    })
    setShowRecordPayment(inv)
  }

  function openUnappliedPayment() {
    // Record a job-level payment not linked to any invoice
    setPaymentForm({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      method: 'cash', reference: '', notes: '',
      invoice_id: null,
    })
    setShowRecordPayment({ _unapplied: true })
  }

  async function savePayment() {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) return
    setPaymentSaving(true)
    const amt = parseFloat(paymentForm.amount)

    const { error } = await supabase.from('invoice_payments').insert([{
      invoice_id: paymentForm.invoice_id || null,
      job_id: id,
      amount: amt,
      payment_date: paymentForm.payment_date,
      method: paymentForm.method,
      reference: paymentForm.reference || null,
      notes: paymentForm.notes || null,
      collected_by: profile?.id,
    }])
    if (error) { alert('Payment failed: ' + error.message); setPaymentSaving(false); return }

    // Build updated local payments for immediate derivation
    const updatedPayments = [
      ...invoicePayments,
      { invoice_id: paymentForm.invoice_id || null, job_id: id, amount: amt },
    ]

    // If applied to an invoice, derive and write new invoice status
    if (paymentForm.invoice_id && !showRecordPayment?._unapplied) {
      const inv = showRecordPayment
      const newApplied = sumPayments(getAppliedPaymentsForInvoice(inv.id, updatedPayments))
      const newStatus = deriveInvoiceStatus(inv, newApplied)
      await supabase.from('invoices').update({ status: newStatus }).eq('id', inv.id)
    }
    // Unapplied payment — no invoice status update

    // Write job payment_status as legacy cache (derived from helper)
    const jf = deriveJobFinancials(job, invoices, updatedPayments)
    await supabase.from('jobs').update({ payment_status: jf.paymentStatus }).eq('id', id)

    setPaymentSaving(false)
    setShowRecordPayment(null)
    fetchAll()
  }

  async function deletePayment(payId) {
    if (!window.confirm('Delete this payment record?')) return

    // Capture the payment before deleting (need its invoice_id)
    const deletedPayment = invoicePayments.find(p => p.id === payId)

    const { error: deleteError } = await supabase.from('invoice_payments').delete().eq('id', payId)
    if (deleteError) { alert('Failed to delete payment.'); return }

    // Build updated local payments without the deleted one
    const updatedPayments = invoicePayments.filter(p => p.id !== payId)

    // If deleted payment was applied to an invoice, recalculate that invoice's status
    if (deletedPayment?.invoice_id) {
      const inv = invoices.find(i => i.id === deletedPayment.invoice_id)
      if (inv) {
        const newApplied = sumPayments(getAppliedPaymentsForInvoice(inv.id, updatedPayments))
        const newStatus = deriveInvoiceStatus(inv, newApplied)
        await supabase.from('invoices').update({ status: newStatus }).eq('id', inv.id)
      }
    }

    // Write job payment_status as legacy cache (derived from helper)
    const jf = deriveJobFinancials(job, invoices, updatedPayments)
    const { error: updateError } = await supabase.from('jobs').update({ payment_status: jf.paymentStatus }).eq('id', id)
    if (updateError) console.error('deletePayment: failed to update payment_status', updateError)

    fetchAll()
  }

  async function applyPaymentToInvoice(paymentId, targetInvoiceId) {
    // Move an unapplied payment to a specific invoice
    setApplyingSaving(true)
    const { error } = await supabase
      .from('invoice_payments')
      .update({ invoice_id: targetInvoiceId })
      .eq('id', paymentId)
    if (error) { alert('Failed to apply payment: ' + error.message); setApplyingSaving(false); return }

    // Recalculate target invoice status with updated payments
    const updatedPayments = invoicePayments.map(p =>
      p.id === paymentId ? { ...p, invoice_id: targetInvoiceId } : p
    )
    const targetInv = invoices.find(i => i.id === targetInvoiceId)
    if (targetInv) {
      const newApplied = sumPayments(getAppliedPaymentsForInvoice(targetInvoiceId, updatedPayments))
      const newStatus = deriveInvoiceStatus(targetInv, newApplied)
      await supabase.from('invoices').update({ status: newStatus }).eq('id', targetInvoiceId)
    }

    // Job payment_status is unchanged (total collected stays same — just moved to an invoice)
    setApplyingSaving(false)
    setShowApplyPayment(null)
    setApplyTargetInvoiceId('')
    fetchAll()
  }

  // ── Payment schedule ─────────────────────────────────────────────────────
  // Over-Contract Invoice Warning (QUOTE_TO_CASH.md) -- a real, confirmed gap, not
  // assumed. Strong warning, not a hard block, per doctrine: the owner can still
  // proceed (e.g. billing extra work without a formal change order first), but
  // shouldn't do it by accident.
  async function saveMilestone() {
    if (!milestoneForm.label || !milestoneForm.amount) return
    const newAmount = parseFloat(milestoneForm.amount)
    const scheduleTotal = paymentSchedule.reduce((sum, m) => sum + m.amount, 0) + newAmount
    if (scheduleTotal > totalContract + 0.01) {
      const over = scheduleTotal - totalContract
      const proceed = window.confirm(
        `This milestone would bring the payment schedule to ${fmt$(scheduleTotal)}, ` +
        `which is ${fmt$(over)} over the current contract amount (${fmt$(totalContract)}). ` +
        `Consider a change order for extra work instead. Add it anyway?`
      )
      if (!proceed) return
    }
    setMilestoneSaving(true)
    const sort = paymentSchedule.length
    const { error } = await supabase.from('job_payment_schedule').insert([{
      job_id: id,
      label: milestoneForm.label,
      amount: parseFloat(milestoneForm.amount),
      due_date: milestoneForm.due_date || null,
      status: 'pending',
      sort_order: sort,
    }])
    if (error) { alert('Failed: ' + error.message); setMilestoneSaving(false); return }
    setMilestoneForm({ label: '', amount: '', due_date: '', status: 'pending' })
    setMilestoneSaving(false)
    setShowAddMilestone(false)
    fetchAll()
  }

  async function updateMilestoneStatus(milestoneId, status) {
    await supabase.from('job_payment_schedule').update({ status }).eq('id', milestoneId)
    fetchAll()
  }

  async function deleteMilestone(milestoneId) {
    if (!window.confirm('Delete this milestone?')) return
    await supabase.from('job_payment_schedule').delete().eq('id', milestoneId)
    fetchAll()
  }

  async function generateMilestoneInvoice(milestone) {
    const dueDate = milestone.due_date || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]
    const { data: inv, error } = await supabase.from('invoices').insert([{
      job_id: id, client_id: job.client_id,
      invoice_number: `INV-${Date.now().toString().slice(-6)}`,
      amount: milestone.amount, status: 'draft', invoice_type: 'milestone',
      due_date: dueDate, notes: milestone.label,
    }]).select().single()
    if (error) { alert('Invoice creation failed: ' + error.message); return }
    await supabase.from('job_payment_schedule').update({ invoice_id: inv.id }).eq('id', milestone.id)
    navigate(`/invoices/${inv.id}`)
  }

  // ── Invoice actions ───────────────────────────────────────────────────────
  async function updateInvoiceStatus(invId, status) {
    await supabase.from('invoices').update({ status, ...(status === 'paid' ? { paid_at: new Date().toISOString() } : {}) }).eq('id', invId)
    fetchAll()
  }

  async function deleteInvoice(invId) {
    if (!window.confirm('Delete this invoice?')) return
    await supabase.from('invoices').delete().eq('id', invId)
    fetchAll()
  }

  async function deleteJob() {
    if (!window.confirm(`Permanently delete "${job.title}"? This cannot be undone.`)) return
    await supabase.from('jobs').delete().eq('id', id)
    navigate('/jobs')
  }

  if (loading) return <div style={s.loading}>Loading...</div>
  if (!job) return <div style={s.loading}>Job not found.</div>

  const clockedInNow = timeEntries.filter(t => !t.clock_out).map(t => t.employee?.id)

  // Design tokens
  const g700 = '#1e4d34', g600 = '#256942', g500 = '#2f8252', g200 = '#a8d5bc', g50 = '#eef8f2'
  const au500 = '#d97706', au600 = '#b45309', au100 = '#fef3c7'
  const r600 = '#dc2626'
  const n900 = '#111827', n700 = '#374151', n500 = '#6b7280', n200 = '#e5e7eb', n100 = '#f3f4f6', n50 = '#f9fafb'
  const cardBg = '#ffffff', borderClr = '#e3e5ea'
  // Balance row summary — accounts for missing-contract edge case.
  // "Paid in Full" only appears when contract is set AND collected >= contract AND not overpaid.
  // Placed here (after color constants) so au500/g500/r600 are in scope.
  const summaryLabel = isOverpaid
    ? 'Credit Balance'
    : (jobFin.isMissingContract && totalCollected > 0)
      ? 'Payment Received — Contract Missing'
      : jobFin.isMissingContract
        ? 'Contract Needed'
        : remainingBalance <= 0
          ? 'Paid in Full'
          : 'Balance Due'
  const summaryValue = isOverpaid
    ? fmt$(creditBalance)
    : (jobFin.isMissingContract && totalCollected > 0)
      ? fmt$(totalCollected)
      : jobFin.isMissingContract
        ? '—'
        : remainingBalance <= 0
          ? '✅'
          : fmt$(remainingBalance)
  const summaryColor = isOverpaid ? g500
    : jobFin.isMissingContract ? au500
    : remainingBalance <= 0 ? g500
    : r600
  const statusBgMap = { active: g600, completed: '#2563eb', pending: au500, cancelled: r600 }
  // Derive overdue status from helper rather than relying on stored invoice.status
  const hasOverdueInvoice = invoices.some(i => {
    const fi = invFin[i.id]
    return deriveInvoiceStatus(i, fi?.appliedTotal || 0) === 'overdue'
  })
  const hasAttention = isAdmin && (
    financialAlerts.length > 0 ||
    hasOverdueInvoice ||
    (job.deposit_amount > 0 && !job.deposit_received)
  )

  return (
    <div style={s.page}>
      <button onClick={() => navigate(-1)} style={s.back}>← Back</button>

      {/* ── Command Header ── */}
      <div style={{ background: cardBg, borderRadius: '12px', borderTop: `3px solid ${g600}`, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: '14px', overflow: 'hidden' }}>

        {/* Top row: identity + actions */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', padding: '20px 24px 18px' }}>

          {/* Left: job identity */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: n900, lineHeight: 1.2 }}>{job.title}</h1>
              <span style={{ background: statusBgMap[job.status] || n500, color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700', textTransform: 'capitalize' }}>{job.status}</span>
              {job.priority && job.priority !== 'normal' && (
                <span style={{ background: PRIORITY_COLOR[job.priority], color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' }}>{job.priority}</span>
              )}
              {job.is_internal && <span style={{ background: n100, color: n500, padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>Internal</span>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: n500 }}>
              {job.client && <ClientLink id={job.client.id} name={`👥 ${job.client.name}`} style={{ color: g600, fontWeight: '600', textDecoration: 'none' }} />}
              {job.client?.phone && <PhoneLink phone={job.client.phone} style={{ color: n500 }} />}
              {job.job_type && <span>🔧 {job.job_type}</span>}
              {job.address && <MapLink address={job.address} style={{ color: n500 }}>📍 {job.address}</MapLink>}
              {job.scheduled_date && <span>📅 {new Date(job.scheduled_date + 'T12:00:00').toLocaleDateString()}</span>}
              {job.end_date && <span>→ {new Date(job.end_date + 'T12:00:00').toLocaleDateString()}</span>}
              {job.crew_size && <span>👷 {job.crew_size} crew</span>}
              {job.estimate && <span style={{ color: '#2563eb' }}>📋 Est: {fmt$(job.estimate.suggested_price)}</span>}
            </div>
          </div>

          {/* Right: action panel (admin only) */}
          {isAdmin && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, width: '230px' }}>
              {/* Primary CTAs */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button style={{ flex: 1, background: au500, color: 'white', border: 'none', padding: '9px 0', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={() => {
                    if (invoices.length === 0) openUnappliedPayment()
                    else if (invoices.length === 1) openRecordPayment(invoices[0])
                    else setTab('invoices')
                  }}>
                  💵 Record Payment
                </button>
                <button style={{ flex: 1, background: g600, color: 'white', border: 'none', padding: '9px 0', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={createOrViewFinalInvoice}>
                  📄 Invoice
                </button>
              </div>
              {/* Work actions */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button style={{ flex: 1, background: 'white', border: `1.5px solid ${n200}`, color: n700, padding: '7px 0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={() => { setShowAddTimeModal(true) }}>
                  ⏱️ Add Time
                </button>
                <button style={{ flex: 1, background: 'white', border: `1.5px solid ${n200}`, color: n700, padding: '7px 0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={() => setTab('costs')}>
                  💰 Add Cost
                </button>
              </div>
              {/* Admin strip */}
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 6px', fontWeight: '600', flexShrink: 0 }} onClick={openEdit}>✏️ Edit</button>
                <select style={{ flex: 1, padding: '5px 6px', borderRadius: '7px', border: `1px solid ${n200}`, fontSize: '12px', fontFamily: 'inherit', color: n700, background: 'white', cursor: 'pointer' }} value={job.status} onChange={e => updateStatus(e.target.value)}>
                  {STATUS_OPTIONS.map(st => <option key={st} value={st}>{cap(st)}</option>)}
                </select>
                <button style={{ background: 'none', border: 'none', color: r600, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 6px', fontWeight: '600', flexShrink: 0 }} onClick={deleteJob}>🗑️</button>
              </div>
            </div>
          )}
          {!isAdmin && (
            <span style={{ background: statusBgMap[job.status] || n500, color: 'white', padding: '5px 14px', borderRadius: '12px', fontSize: '13px', fontWeight: '700', textTransform: 'capitalize', flexShrink: 0 }}>{job.status}</span>
          )}
        </div>

      </div>

      {/* ── KPI Strip ── */}
      {isAdmin && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: '8px', marginBottom: '14px' }}>
          {[
            { label: 'Current Contract', value: fmt$(totalContract), sub: changeOrderTotal > 0 ? `+${fmt$(changeOrderTotal)} CO` : null },
            { label: 'Invoiced',         value: fmt$(totalInvoiced) },
            { label: 'Amount Paid',      value: fmt$(totalCollected), accent: g500 },
            isOverpaid
              ? { label: 'Credit Balance', value: fmt$(creditBalance), accent: g500 }
              : { label: 'Balance Due',    value: fmt$(remainingBalance), accent: remainingBalance > 0 ? r600 : g500 },
            { label: 'Actual Cost',      value: fmt$(totalAllCosts) },
            { label: 'Gross Profit',     value: fmt$(profit), accent: profit >= 0 ? g500 : r600 },
            { label: 'Margin %',         value: `${margin.toFixed(1)}%`, accent: margin >= 20 ? g500 : margin > 0 ? au500 : r600 },
            { label: 'Labor Hours',      value: fmtH(totalHours), sub: job.estimated_hours ? `of ${fmtH(job.estimated_hours)}` : null, accent: job.estimated_hours > 0 && totalHours > job.estimated_hours ? r600 : undefined },
          ].map(k => (
            <div key={k.label} style={{ background: cardBg, borderRadius: '10px', padding: '12px 10px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${k.accent || borderClr}` }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: k.accent || n900, fontFamily: 'monospace', lineHeight: 1.2 }}>{k.value}</div>
              <div style={{ fontSize: '10px', color: n500, textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '3px' }}>{k.label}</div>
              {k.sub && <div style={{ fontSize: '10px', color: n500, marginTop: '2px' }}>{k.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div style={{ position: 'sticky', top: '56px', zIndex: 10, background: '#f1f3f6', borderBottom: `1px solid ${borderClr}`, marginBottom: '16px', display: 'flex', overflowX: 'auto' }}>
        {(isAdmin
          ? [
              { key: 'overview',   label: 'Overview' },
              { key: 'financials', label: 'Financials' },
              { key: 'pva',        label: 'Planned vs Actual' },
              { key: 'invoices',   label: 'Invoices' },
              { key: 'payments',   label: 'Payments' },
              { key: 'time',       label: 'Time' },
              { key: 'costs',      label: 'Costs' },
              { key: 'documents',  label: 'Documents & History' },
              { key: 'photos',     label: 'Photos & Notes' },
            ]
          : [
              { key: 'overview', label: 'Overview' },
              { key: 'time',     label: 'Time' },
              { key: 'photos',   label: 'Photos & Notes' },
            ]
        ).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '0 18px', height: '46px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: tab === t.key ? '700' : '500', color: tab === t.key ? g600 : n500, borderBottom: tab === t.key ? `2.5px solid ${g600}` : '2.5px solid transparent', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview tab ── */}
      {tab === 'overview' && (
        <div style={isAdmin ? { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: '14px', alignItems: 'flex-start' } : {}}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {hasAttention && (
              <div style={{ background: cardBg, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ background: au100, borderBottom: `1px solid #edd890`, padding: '8px 14px', fontSize: '11px', fontWeight: '700', color: au600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Needs Attention</div>
                <div style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {/* Financial alerts from helper */}
                  {financialAlerts.map((alert, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: n700 }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: alert.severity === 'error' ? r600 : au500, flexShrink: 0, display: 'inline-block' }} />
                      <span>{alert.message}</span>
                      {alert.action === 'apply_payment' && (
                        <button style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${au500}`, color: au500, padding: '2px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }} onClick={() => setTab('payments')}>Reconcile</button>
                      )}
                      {alert.action === 'edit_contract' && (
                        <button style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${au500}`, color: au500, padding: '2px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }} onClick={openEdit}>Edit Job</button>
                      )}
                      {alert.action === 'review_payments' && (
                        <button style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${au500}`, color: au500, padding: '2px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }} onClick={() => setTab('payments')}>Review</button>
                      )}
                    </div>
                  ))}
                  {/* Overdue invoices (derived, not from stored status) */}
                  {hasOverdueInvoice && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: n700 }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: au500, flexShrink: 0, display: 'inline-block' }} />
                      <span>Overdue invoice(s)</span>
                      <button style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${au500}`, color: au500, padding: '2px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }} onClick={() => setTab('invoices')}>View</button>
                    </div>
                  )}
                  {/* Deposit pending */}
                  {job.deposit_amount > 0 && !job.deposit_received && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: n700 }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: au500, flexShrink: 0, display: 'inline-block' }} />
                      <span>Deposit pending: {fmt$(job.deposit_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {job.description && (
              <div style={{ background: cardBg, borderRadius: '10px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: n500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Scope of Work</div>
                <div style={{ fontSize: '14px', color: n700, lineHeight: 1.6 }}>{job.description}</div>
                {job.estimate && (
                  <div style={{ marginTop: '10px', padding: '8px 12px', background: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#2563eb' }}>
                    📋 Linked Estimate — {job.estimate.job_type} · {fmt$(job.estimate.suggested_price)}
                  </div>
                )}
              </div>
            )}
            <div style={{ background: cardBg, borderRadius: '10px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: n500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Job Details</div>
                <Link to={`/schedule?job=${id}`} style={{ fontSize: '12px', fontWeight: '600', color: g600, textDecoration: 'none', background: g50, padding: '5px 10px', borderRadius: '7px' }}>📅 Schedule This Job</Link>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                <Detail label="Client">{job.client ? <ClientLink id={job.client.id} name={job.client.name} style={{ color: g600, fontWeight: '600' }} /> : null}</Detail>
                <Detail label="Type" value={job.job_type} />
                <Detail label="Status" value={cap(job.status)} />
                <Detail label="Priority" value={cap(job.priority)} />
                <Detail label="Start Date" value={job.scheduled_date ? new Date(job.scheduled_date + 'T12:00:00').toLocaleDateString() : null} />
                <Detail label="End Date" value={job.end_date ? new Date(job.end_date + 'T12:00:00').toLocaleDateString() : null} />
                <Detail label="Start Time" value={job.start_time} />
                <Detail label="Crew Size" value={job.crew_size ? `${job.crew_size} people` : null} />
                <Detail label="Address">{job.address || job.client?.address ? <MapLink address={job.address || job.client?.address} style={{ color: g600, fontWeight: '600' }}>{job.address || job.client?.address}</MapLink> : null}</Detail>
                {job.is_recurring && <Detail label="Recurring" value={`✅ ${cap(job.recurrence_frequency || 'yes')}`} />}
              </div>
            </div>
            {isAdmin && (
              <RemindersPanel jobId={id} clientId={job.client?.id} title="Follow-Ups" />
            )}
            {isAdmin && (
              <div style={{ background: '#fffbf0', border: '1px solid #edd890', borderRadius: '10px', padding: '16px 18px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b3a08', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>🔒 Internal Notes</div>
                <div style={{ marginBottom: '10px' }}>
                  {job.notes ? job.notes.split('\n\n').map((n, i) => (
                    <div key={i} style={{ background: 'white', borderRadius: '7px', padding: '8px 12px', marginBottom: '6px', fontSize: '13px', color: n700, whiteSpace: 'pre-line', border: '1px solid #edd890' }}>{n}</div>
                  )) : <p style={{ fontSize: '13px', color: n500, margin: 0 }}>No internal notes yet.</p>}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <textarea style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: `1.5px solid ${n200}`, fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', minHeight: '56px' }} placeholder="Add crew instruction or internal note..." value={note} onChange={e => setNote(e.target.value)} rows={2} />
                  <button style={{ background: '#6b3a08', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', alignSelf: 'flex-end' }} onClick={saveNote}>Save</button>
                </div>
              </div>
            )}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: cardBg, borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: n500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Crew & Schedule</div>
                {job.assignments?.length === 0 && <p style={{ fontSize: '13px', color: n500, margin: '0 0 10px' }}>No crew assigned.</p>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: job.assignments?.length > 0 ? '10px' : '0' }}>
                  {(job.assignments || []).map(a => {
                    const isClockedIn = clockedInNow.includes(a.employee?.id)
                    return (
                      <div key={a.employee?.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: g50, border: `1px solid ${g200}`, color: g600, padding: '5px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                        <span>{isClockedIn ? '🟢' : '👷'} {a.employee?.full_name}</span>
                        <button style={{ background: 'none', border: 'none', color: n500, cursor: 'pointer', fontSize: '12px', padding: '0 2px', fontFamily: 'inherit' }} onClick={() => removeEmployee(a.employee?.id)}>✕</button>
                      </div>
                    )
                  })}
                </div>
                {unassignedEmployees.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: n500, marginBottom: '6px' }}>Add crew:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {unassignedEmployees.map(emp => (
                        <button key={emp.id} style={{ background: 'white', border: `1.5px solid ${g200}`, color: g600, padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => assignEmployee(emp.id)} disabled={assignSaving}>+ {emp.full_name}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ background: cardBg, borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: n500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Payment Status</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: n500 }}>Contract</span>
                    <span style={{ fontWeight: '700', color: n900 }}>{jobFin.isMissingContract ? <span style={{ color: au500 }}>Not set</span> : fmt$(totalContract)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: n500 }}>Collected</span>
                    <span style={{ fontWeight: '700', color: g500 }}>{fmt$(totalCollected)}</span>
                  </div>
                  <div style={{ borderTop: `1px solid ${n200}`, paddingTop: '6px', display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: n500, fontWeight: '600' }}>
                      {summaryLabel}
                    </span>
                    <span style={{ fontWeight: '700', color: summaryColor }}>
                      {summaryValue}
                    </span>
                  </div>
                  {/* Derived payment status badge — read-only, not a dropdown */}
                  <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '11px', color: n500 }}>Status:</span>
                    <span style={{
                      padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', textTransform: 'capitalize',
                      background: jobFin.paymentStatus === 'paid' ? '#dcfce7' : jobFin.paymentStatus === 'overpaid' ? '#dcfce7' : jobFin.paymentStatus === 'partial' ? au100 : n100,
                      color: jobFin.paymentStatus === 'paid' ? g500 : jobFin.paymentStatus === 'overpaid' ? g700 : jobFin.paymentStatus === 'partial' ? au600 : n500,
                    }}>
                      {jobFin.paymentStatus === 'overpaid' ? 'Overpaid' : cap(jobFin.paymentStatus || 'unpaid')}
                    </span>
                    <span style={{ fontSize: '11px', color: n500, marginLeft: 'auto', fontStyle: 'italic' }}>Derived</span>
                  </div>
                </div>
              </div>
              <div style={{ background: g50, border: `1px solid ${g200}`, borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: g700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>📢 Client Updates</div>
                <div style={{ fontSize: '12px', color: g600 }}>Client portal coming soon.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Financials tab ── */}
      {tab === 'financials' && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <h3 style={s.sectionTitle}>Payment Summary</h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: n700, cursor: 'pointer' }}>
                <input type="checkbox" checked={taxEnabled} onChange={e => setTaxEnabled(e.target.checked)} />
                Tax (8.25%)
              </label>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <FinRow label="Base Contract" value={fmt$(baseContract)} />
              {changeOrderTotal > 0 && <FinRow label="Change Orders" value={`+${fmt$(changeOrderTotal)}`} color={au500} />}
              {taxEnabled && <FinRow label="Tax (8.25%)" value={fmt$(taxAmount)} color={n500} />}
              <div style={{ borderTop: `1px solid ${n200}`, paddingTop: '8px', marginTop: '2px' }}>
                <FinRow label="Total Contract" value={fmt$(totalContract)} bold />
              </div>
              {job?.deposit_amount > 0 && <FinRow label={`Deposit ${job.deposit_received ? '✅ Received' : '⏳ Pending'}`} value={fmt$(job.deposit_amount)} color={job.deposit_received ? g500 : au500} />}
              <div style={{ borderTop: `1px solid ${n200}`, paddingTop: '8px', marginTop: '2px' }}>
                <FinRow label="Total Collected" value={fmt$(totalCollected)} color={g500} bold />
                <FinRow
                  label={
                    isOverpaid ? '⚡ Credit Balance'
                    : (jobFin.isMissingContract && totalCollected > 0) ? '⚠️ Payment Received — Contract Missing'
                    : jobFin.isMissingContract ? '⚠️ Contract Needed'
                    : remainingBalance <= 0 ? '✅ Paid in Full'
                    : '🔴 Balance Due'
                  }
                  value={summaryValue}
                  color={summaryColor}
                  bold
                />
              </div>
            </div>
          </div>
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <h3 style={s.sectionTitle}>Payment Schedule</h3>
              <button style={s.addCrewBtn} onClick={() => setShowAddMilestone(true)}>+ Add Milestone</button>
            </div>
            {paymentSchedule.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={s.noData}>No payment schedule set. Add milestones or use the quick templates below.</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { label: '50% / 50%', items: [{ label: '50% Deposit', pct: 0.5 }, { label: '50% Final', pct: 0.5 }] },
                    { label: '35% / 65%', items: [{ label: '35% Deposit', pct: 0.35 }, { label: '65% Final', pct: 0.65 }] },
                    { label: '15% / 35% / 50%', items: [{ label: '15% at Signing', pct: 0.15 }, { label: '35% at Start', pct: 0.35 }, { label: '50% Final', pct: 0.50 }] },
                    { label: '100% Upfront', items: [{ label: '100% Due Upfront', pct: 1.0 }] },
                  ].map(tmpl => (
                    <button key={tmpl.label} style={s.templateBtn} onClick={async () => {
                      for (let i = 0; i < tmpl.items.length; i++) {
                        const item = tmpl.items[i]
                        await supabase.from('job_payment_schedule').insert([{ job_id: id, label: item.label, amount: parseFloat((totalContract * item.pct).toFixed(2)), status: 'pending', sort_order: i }])
                      }
                      fetchAll()
                    }}>{tmpl.label}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {paymentSchedule.map((ms) => (
                  <div key={ms.id} style={s.milestoneRow}>
                    <div style={s.milestoneLeft}>
                      <span style={{ ...s.msBadge, background: ms.status === 'paid' ? g500 : ms.status === 'partial' ? au500 : n500 }}>{cap(ms.status)}</span>
                      <div>
                        <div style={{ fontWeight: '600', color: n900, fontSize: '14px' }}>{ms.label}</div>
                        {ms.due_date && <div style={{ fontSize: '12px', color: n500 }}>Due {new Date(ms.due_date + 'T12:00:00').toLocaleDateString()}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '700', fontSize: '15px' }}>{fmt$(ms.amount)}</span>
                      <select style={{ ...s.paymentSelect, fontSize: '12px' }} value={ms.status} onChange={e => updateMilestoneStatus(ms.id, e.target.value)}>
                        {['pending', 'partial', 'paid'].map(st => <option key={st} value={st}>{cap(st)}</option>)}
                      </select>
                      {!ms.invoice_id && <button style={{ ...s.addCrewBtn, fontSize: '12px', padding: '5px 10px' }} onClick={() => generateMilestoneInvoice(ms)}>📄 Invoice</button>}
                      {ms.invoice_id && <span style={{ fontSize: '11px', color: g500, fontWeight: '700' }}>✅ Invoiced</span>}
                      <button style={s.deleteSmallBtn} onClick={() => deleteMilestone(ms.id)}>✕</button>
                    </div>
                  </div>
                ))}
                <div style={{ borderTop: `1px solid ${n200}`, paddingTop: '8px', marginTop: '4px' }}>
                  <FinRow label="Schedule Total" value={fmt$(paymentSchedule.reduce((acc, m) => acc + m.amount, 0))} bold />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Planned vs Actual tab ── */}
      {tab === 'pva' && isAdmin && (() => {
        const hasPlan  = job.planned_revenue > 0
        const pvaRows  = hasPlan ? derivePvaRows(job, { totalHours, totalLaborCost, totalMaterialCost, totalAllCosts, profit, margin }, totalContract) : null
        const pvaSumm  = pvaRows ? derivePvaSummary(pvaRows) : null
        const fPva     = (v, fmt) => v == null ? '—' : fmt === '$' ? fmt$(v) : fmt === 'h' ? fmtH(v) : `${Number(v).toFixed(1)}%`
        const fDiff    = (diff, fmt) => `${diff >= 0 ? '+' : ''}${fPva(diff, fmt)}`

        return (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>Planned vs Actual</h3>

            {!hasPlan ? (
              <div>
                <div style={s.warnBox}>⚠️ No planned values — this job was created without a linked estimate.</div>
                <p style={{ fontSize: '13px', color: n500, margin: '10px 0 0', lineHeight: '1.5' }}>
                  Planned values are set when a job is created by converting an estimate. Go to the Estimates page, create an estimate, then use "Convert to Job" to populate planned revenue, costs, hours, and margin automatically.
                </p>
              </div>
            ) : (
              <>
                {/* ── Summary cards ── */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
                  {[
                    { label: 'Revenue',     v: pvaSumm.revenue, fmt: '$' },
                    { label: 'Total Cost',  v: pvaSumm.cost,    fmt: '$' },
                    { label: 'Profit',      v: pvaSumm.profit,  fmt: '$' },
                    { label: 'Margin',      v: pvaSumm.margin,  fmt: '%' },
                  ].map(({ label, v, fmt }) => {
                    const accent = v ? (v.good ? g500 : r600) : borderClr
                    const valStr = v == null ? '—' : fDiff(v.diff, fmt)
                    const subStr = v == null ? 'No plan'
                                : v.diff === 0 ? 'On Track'
                                : `${v.good ? '✓ Favorable' : '✗ Unfavorable'} · ${v.pct}%`
                    return (
                      <div key={label} style={{ background: cardBg, border: `1px solid ${borderClr}`, borderRadius: '9px', padding: '12px 14px', borderTop: `3px solid ${accent}` }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: n500, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>{label}</div>
                        <div style={{ fontSize: '17px', fontWeight: '700', color: v ? accent : n500, fontFamily: 'monospace', lineHeight: 1.2 }}>{valStr}</div>
                        <div style={{ fontSize: '11px', color: v ? accent : n500, marginTop: '3px' }}>{subStr}</div>
                      </div>
                    )
                  })}
                </div>

                {/* ── Detail table ── */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px' }}>
                    <thead>
                      <tr style={{ background: n50, borderBottom: `1px solid ${n200}` }}>
                        {[['Metric', 'left'], ['Planned', 'right'], ['Actual', 'right'], ['Difference', 'right'], ['Status', 'center']].map(([h, align]) => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: align, fontWeight: '700', color: n500, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pvaRows.map(row => {
                        const diff  = (row.actual || 0) - (row.planned || 0)
                        const pct   = row.planned > 0 ? Math.abs(diff / row.planned * 100).toFixed(1) : null
                        const good  = row.better === 'high' ? diff >= 0 : diff <= 0
                        const fc    = diff === 0 ? n500 : good ? g500 : r600
                        const badge = !(row.planned > 0) ? null
                                    : diff === 0 ? { label: 'On Track',    color: n500,  bg: n100 }
                                    : good       ? { label: 'Favorable',   color: g500,  bg: '#f0f9f4' }
                                    :              { label: 'Unfavorable', color: r600,  bg: '#fef2f2' }
                        return (
                          <tr key={row.label} style={{ borderBottom: `1px solid ${n100}` }}>
                            <td style={{ padding: '12px 14px', fontWeight: '500', color: n900 }}>{row.label}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: n700, fontFamily: 'monospace' }}>{fPva(row.planned, row.format)}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: n900, fontFamily: 'monospace' }}>{fPva(row.actual, row.format)}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '600', color: fc, fontFamily: 'monospace' }}>
                              {row.planned > 0 ? `${fDiff(diff, row.format)}${pct ? ` (${pct}%)` : ''}` : '—'}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                              {badge
                                ? <span style={{ fontSize: '11px', fontWeight: '600', color: badge.color, background: badge.bg, padding: '3px 9px', borderRadius: '100px' }}>{badge.label}</span>
                                : <span style={{ color: n500 }}>—</span>}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )
      })()}

      {/* ── Invoices tab ── */}
      {tab === 'invoices' && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px' }}>
            {[
              { label: 'Invoiced',     value: fmt$(totalInvoiced) },
              { label: 'Collected',    value: fmt$(totalCollected), accent: g500 },
              { label: 'Outstanding',  value: fmt$(Math.max(0, totalInvoiced - totalCollected)), accent: r600 },
              { label: 'Invoices',     value: String(invoices.length) },
            ].map(k => (
              <div key={k.label} style={{ background: cardBg, borderRadius: '10px', padding: '14px 12px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${k.accent || borderClr}` }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: k.accent || n900, fontFamily: 'monospace' }}>{k.value}</div>
                <div style={{ fontSize: '10px', color: n500, textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '4px' }}>{k.label}</div>
              </div>
            ))}
          </div>
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <h3 style={s.sectionTitle}>Invoices</h3>
              <button style={s.changeOrderBtn} onClick={() => setShowChangeOrder(true)}>+ Change Order</button>
            </div>
            {invoices.length === 0 ? (
              <p style={s.noData}>No invoices yet. Click "📄 Invoice" above to create one, or add a change order.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {invoices.map(inv => {
                  const fi = invFin[inv.id] || { appliedTotal: 0, balance: 0, invoiceTotal: 0 }
                  const derivedStatus = deriveInvoiceStatus(inv, fi.appliedTotal)
                  const isOverdue = derivedStatus === 'overdue'
                  const statusColors = {
                    paid: { bg: '#dcfce7', color: g500 }, partial: { bg: au100, color: au600 },
                    overdue: { bg: '#fef2f2', color: r600 }, draft: { bg: n100, color: n500 },
                    sent: { bg: '#dbeafe', color: '#2563eb' }, void: { bg: n100, color: n500 },
                    cancelled: { bg: n100, color: n500 },
                  }
                  const sc = statusColors[derivedStatus] || { bg: n100, color: n500 }
                  return (
                    <div key={inv.id} style={{ background: n50, borderRadius: '10px', overflow: 'hidden', border: `1px solid ${isOverdue ? r600 : n200}`, boxShadow: isOverdue ? `inset 3px 0 0 ${r600}` : 'none' }}>
                      <div style={s.invoiceRow}>
                        <div style={s.invoiceLeft}>
                          <span style={{ ...s.invoiceTypeBadge, background: inv.invoice_type === 'change_order' ? au500 : inv.invoice_type === 'milestone' ? '#7c3aed' : '#2563eb' }}>
                            {inv.invoice_type === 'change_order' ? 'Change Order' : inv.invoice_type === 'milestone' ? 'Milestone' : 'Invoice'}
                          </span>
                          <div>
                            <Link to={`/invoices/${inv.id}`} style={{ fontWeight: '600', color: '#1d4ed8', fontSize: '14px', textDecoration: 'none' }}>{inv.invoice_number} →</Link>
                            {inv.notes && <div style={{ fontSize: '12px', color: n500 }}>{inv.notes}</div>}
                            <div style={{ fontSize: '12px', color: isOverdue ? r600 : n500 }}>Due {inv.due_date ? new Date(inv.due_date + 'T12:00:00').toLocaleDateString() : '—'}</div>
                          </div>
                        </div>
                        <div style={s.invoiceRight}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px' }}>{fmt$(fi.invoiceTotal)}</div>
                            {fi.appliedTotal > 0 && <div style={{ fontSize: '11px', color: g500 }}>{fmt$(fi.appliedTotal)} collected</div>}
                            {fi.balance > 0 && <div style={{ fontSize: '11px', color: r600 }}>{fmt$(fi.balance)} remaining</div>}
                          </div>
                          {/* Derived status badge — read-only financial states; lifecycle states editable */}
                          <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', textTransform: 'capitalize', background: sc.bg, color: sc.color }}>{derivedStatus}</span>
                          {/* Allow user to set lifecycle states (draft/sent/void) but NOT paid/partial/overdue */}
                          <select style={{ ...s.paymentSelect, fontSize: '12px' }} value={inv.status === 'void' || inv.status === 'cancelled' ? inv.status : 'open'} onChange={e => {
                            if (e.target.value === 'void' || e.target.value === 'cancelled') updateInvoiceStatus(inv.id, e.target.value)
                            else if (e.target.value === 'draft' || e.target.value === 'sent') updateInvoiceStatus(inv.id, e.target.value)
                          }}>
                            <option value="open">Lifecycle…</option>
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="void">Void</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <button style={{ ...s.addCrewBtn, fontSize: '12px', padding: '6px 10px', background: '#0369a1' }} onClick={() => openRecordPayment(inv)}>💵 Record</button>
                          <button style={s.deleteSmallBtn} onClick={() => deleteInvoice(inv.id)}>✕</button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Payments tab ── */}
      {tab === 'payments' && isAdmin && (
        <div style={s.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ ...s.sectionTitle, margin: 0 }}>Payment Ledger</h3>
            <button style={{ background: au500, color: 'white', border: 'none', padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }} onClick={openUnappliedPayment}>
              + Record Unapplied Payment
            </button>
          </div>
          {invoicePayments.length === 0 ? (
            <p style={s.noData}>No payments recorded yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Payments grouped by invoice */}
              {invoices.map(inv => {
                const invPayments = invoicePayments.filter(p => p.invoice_id === inv.id)
                if (invPayments.length === 0) return null
                return (
                  <div key={inv.id}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: n500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{inv.invoice_number}</div>
                    {invPayments.map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: n50, borderRadius: '8px', marginBottom: '6px', fontSize: '13px' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <span style={{ color: n500 }}>{new Date(p.payment_date + 'T12:00:00').toLocaleDateString()}</span>
                          <span style={{ ...s.msBadge, background: '#0369a1', fontSize: '10px' }}>{cap(p.method)}</span>
                          {p.reference && <span style={{ color: n500 }}>#{p.reference}</span>}
                          {p.collector && <span style={{ color: n500 }}>by {p.collector.full_name}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '700', color: g500 }}>{fmt$(p.amount)}</span>
                          <button style={{ ...s.deleteSmallBtn, fontSize: '11px', padding: '2px 6px' }} onClick={() => deletePayment(p.id)}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}

              {/* Unapplied payments section */}
              {jobFin.unappliedPayments?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: au600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>⚠️ Unapplied Payments</div>
                  <div style={{ background: au100, border: `1px solid #edd890`, borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: au600, marginBottom: '8px' }}>
                    These payments count toward job balance but are not applied to any invoice. Apply to reconcile.
                  </div>
                  {jobFin.unappliedPayments.map(p => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#fffbf0', border: `1px solid #edd890`, borderRadius: '8px', marginBottom: '6px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <span style={{ color: n500 }}>{new Date(p.payment_date + 'T12:00:00').toLocaleDateString()}</span>
                        <span style={{ ...s.msBadge, background: '#0369a1', fontSize: '10px' }}>{cap(p.method)}</span>
                        {p.reference && <span style={{ color: n500 }}>#{p.reference}</span>}
                        {p.collector?.full_name && <span style={{ color: n500 }}>by {p.collector.full_name}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontWeight: '700', color: g500 }}>{fmt$(p.amount)}</span>
                        {invoices.filter(i => i.status !== 'void' && i.status !== 'cancelled').length > 0 && (
                          <button style={{ background: g600, color: 'white', border: 'none', padding: '3px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }}
                            onClick={() => { setShowApplyPayment(p); setApplyTargetInvoiceId('') }}>
                            Apply to Invoice
                          </button>
                        )}
                        <button style={{ ...s.deleteSmallBtn, fontSize: '11px', padding: '2px 6px' }} onClick={() => deletePayment(p.id)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Totals */}
              <div style={{ borderTop: `2px solid ${n200}`, paddingTop: '10px', marginTop: '4px' }}>
                <FinRow label="Total Collected" value={fmt$(totalCollected)} color={g500} bold />
                {jobFin.unappliedPayments?.length > 0 && (
                  <FinRow label={`Unapplied (${jobFin.unappliedPayments.length})`} value={fmt$(jobFin.totalUnapplied)} color={au500} />
                )}
                {isOverpaid
                  ? <FinRow label="⚡ Credit Balance" value={fmt$(creditBalance)} color={g500} bold />
                  : <FinRow
                      label={
                        (jobFin.isMissingContract && totalCollected > 0) ? '⚠️ Payment Received — Contract Missing'
                        : jobFin.isMissingContract ? '⚠️ Contract Needed'
                        : remainingBalance <= 0 ? '✅ Paid in Full'
                        : 'Balance Due'
                      }
                      value={summaryValue}
                      color={summaryColor}
                      bold
                    />
                }
              </div>

              {/* Overpayment callout */}
              {isOverpaid && (
                <div style={{ background: '#f0fdf4', border: `1px solid ${g200}`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: g700, display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span>⚡</span>
                  <span><strong>Credit balance: {fmt$(creditBalance)}</strong> — more has been collected than the total contract. Review payments or update the contract amount.</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Time tab ── */}
      {tab === 'time' && (
        <div style={s.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ ...s.sectionTitle, margin: 0 }}>Time Entries</h3>
            {isAdmin && (
              <button style={{ background: g600, color: 'white', border: 'none', padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => setShowAddTimeModal(true)}>
                + Add Time Entry
              </button>
            )}
          </div>
          {timeEntries.length === 0 ? <p style={s.noData}>No time logged yet.</p> : (
            <div style={s.timeList}>
              {timeEntries.map(t => {
                const hrs = t.clock_out ? ((new Date(t.clock_out) - new Date(t.clock_in)) / 3600000).toFixed(2) : null
                const laborAmt = hrs && t.employee?.hourly_rate ? (parseFloat(hrs) * t.employee.hourly_rate).toFixed(2) : null
                if (isMobile) {
                  return (
                    <div key={t.id} style={s.timeRowMobile}>
                      <div style={s.timeRowMobileTop}>
                        <span style={s.timeName}>👷 {t.employee?.full_name}</span>
                        <span style={s.timeHours}>{hrs ? `${hrs}h` : '—'}{isAdmin && laborAmt ? ` · $${laborAmt}` : ''}</span>
                      </div>
                      <div style={s.timeRowMobileMeta}>
                        In: {new Date(t.clock_in).toLocaleString()}<br />
                        Out: {t.clock_out ? new Date(t.clock_out).toLocaleString() : '⏱️ Active'}
                      </div>
                    </div>
                  )
                }
                return (
                  <div key={t.id} style={s.timeRow}>
                    <span style={s.timeName}>👷 {t.employee?.full_name}</span>
                    <span style={s.timeClock}>{new Date(t.clock_in).toLocaleString()}</span>
                    <span style={s.timeClock}>{t.clock_out ? new Date(t.clock_out).toLocaleString() : '⏱️ Active'}</span>
                    <span style={s.timeHours}>{hrs ? `${hrs}h` : '—'}</span>
                    {isAdmin && <span style={s.timeHours}>{laborAmt ? `$${laborAmt}` : '—'}</span>}
                  </div>
                )
              })}
              <div style={{ ...s.totalRow, fontSize: '13px' }}>
                <strong>Total: {fmtH(totalHours)} · Labor Cost: {fmt$(totalLaborCost)}</strong>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Costs tab ── */}
      {tab === 'costs' && isAdmin && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Job Costs</h3>
          <div style={s.addCostForm}>
            <select style={s.select} value={newCost.category} onChange={e => setNewCost(c => ({ ...c, category: e.target.value }))}>
              {COST_CATEGORIES.map(cat => <option key={cat} value={cat}>{cap(cat.replace('_', ' '))}</option>)}
            </select>
            <input style={s.costInput} placeholder="Description *" value={newCost.description} onChange={e => setNewCost(c => ({ ...c, description: e.target.value }))} />
            <input style={{ ...s.costInput, maxWidth: '110px' }} placeholder="Total $" type="number" value={newCost.amount} onChange={e => setNewCost(c => ({ ...c, amount: e.target.value }))} />
            <input style={{ ...s.costInput, maxWidth: '80px' }} placeholder="Qty" type="number" value={newCost.quantity} onChange={e => setNewCost(c => ({ ...c, quantity: e.target.value }))} />
            <input style={{ ...s.costInput, maxWidth: '100px' }} placeholder="Unit $" type="number" step="0.01" value={newCost.unit_cost} onChange={e => setNewCost(c => ({ ...c, unit_cost: e.target.value }))} />
            <button style={s.addCostBtn} onClick={addCost} disabled={addingCost || !newCost.description || !newCost.amount}>{addingCost ? 'Adding...' : '+ Add'}</button>
          </div>
          {costError && <div style={s.errorBox}>{costError}</div>}
          <div style={s.costList}>
            {costs.map(c => (
              <div key={c.id} style={s.costRow}>
                <span style={{ ...s.costCat, background: CAT_COLOR[c.category] || n500 }}>{c.category.replace('_', ' ')}</span>
                <span style={s.costDesc}>{c.description}</span>
                {c.quantity && <span style={{ fontSize: '12px', color: n500 }}>×{c.quantity}</span>}
                <span style={s.costAmt}>{fmt$(c.amount)}</span>
                <button style={s.deleteCostBtn} onClick={() => deleteCost(c.id)}>✕</button>
              </div>
            ))}
            {costs.length === 0 && <p style={s.noData}>No costs added yet.</p>}
          </div>
          <div style={s.totalRow}>
            <FinRow label="Materials"   value={fmt$(totalMaterialCost)} />
            <FinRow label="Other Costs" value={fmt$(totalOtherCost)} />
            <FinRow label="Labor"       value={`${fmt$(totalLaborCost)} (${fmtH(totalHours)})`} />
            <FinRow label="Total Cost"  value={fmt$(totalAllCosts)} bold />
            <FinRow label="Profit"      value={fmt$(profit)} color={profit >= 0 ? g500 : r600} bold />
          </div>
        </div>
      )}

      {/* ── Documents & History tab (Phase 4.2) ── */}
      {tab === 'documents' && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={s.section}>
            <h3 style={s.sectionTitle}>Source & Approval</h3>
            {!job.estimate_id && !job.proposal_id ? (
              <p style={s.noData}>This job wasn't created from an estimate or proposal — likely a manual job.</p>
            ) : (
              <div style={s.docList}>
                {job.estimate_id && (
                  <Link to={`/estimates/${job.estimate_id}/edit`} style={s.docRow}>
                    <span>📋 Source Estimate{job.estimate?.job_type ? ` — ${job.estimate.job_type}` : ''}</span>
                    <span style={s.docRowMeta}>View →</span>
                  </Link>
                )}
                {job.sourceSiteVisit && (
                  <Link to={`/site-visits/${job.sourceSiteVisit.id}`} style={s.docRow}>
                    <span>🏠 Source Site Visit{job.sourceSiteVisit.title ? ` — ${job.sourceSiteVisit.title}` : ''}</span>
                    <span style={s.docRowMeta}>View →</span>
                  </Link>
                )}
                {job.proposal_id && job.proposal && (
                  <Link to={`/proposals/${job.proposal_id}`} style={s.docRow}>
                    <span>📄 Proposal {job.proposal.proposal_number}</span>
                    <span style={{ ...s.docRowMeta, color: job.proposal.status === 'approved' ? g500 : job.proposal.status === 'declined' ? r600 : n500 }}>
                      {job.proposal.status === 'approved'
                        ? `Approved ${new Date(job.proposal.approved_at).toLocaleDateString()} (${job.proposal.approval_source}) · ${fmt$(job.proposal.approved_contract_amount)}`
                        : job.proposal.status === 'declined'
                        ? `Declined ${new Date(job.proposal.declined_at).toLocaleDateString()}`
                        : cap(job.proposal.status)} →
                    </span>
                  </Link>
                )}
                {job.proposal_id && !job.proposal && (
                  <p style={s.noData}>Linked proposal could not be loaded.</p>
                )}
              </div>
            )}
          </div>

          <div style={s.section}>
            <h3 style={s.sectionTitle}>Invoices ({invoices.length})</h3>
            {invoices.length === 0 ? <p style={s.noData}>No invoices yet.</p> : (
              <div style={s.docList}>
                {invoices.map(inv => (
                  <div key={inv.id} style={s.docRow}>
                    <span>🧾 {inv.invoice_number || 'Invoice'}</span>
                    <span style={s.docRowMeta}>{fmt$(getInvoiceTotal(inv))} · {inv.status}</span>
                  </div>
                ))}
              </div>
            )}
            <button style={s.docTabLink} onClick={() => setTab('invoices')}>View Invoices tab →</button>
          </div>

          <div style={s.section}>
            <h3 style={s.sectionTitle}>Payments ({invoicePayments.length})</h3>
            {invoicePayments.length === 0 ? (
              <p style={s.noData}>No payments recorded yet.</p>
            ) : (
              <p style={{ fontSize: '13px', color: n700, margin: '0 0 10px' }}>{invoicePayments.length} payment(s) totaling {fmt$(sumPayments(invoicePayments))}.</p>
            )}
            <button style={s.docTabLink} onClick={() => setTab('payments')}>View Payments tab →</button>
          </div>

          <div style={s.section}>
            <h3 style={s.sectionTitle}>Change Orders {changeOrderTotal > 0 ? `(${fmt$(changeOrderTotal)})` : ''}</h3>
            {invoices.filter(inv => inv.invoice_type === 'change_order').length === 0 ? (
              <p style={s.noData}>No change orders on this job.</p>
            ) : (
              <div style={s.docList}>
                {invoices.filter(inv => inv.invoice_type === 'change_order').map(inv => (
                  <div key={inv.id} style={s.docRow}>
                    <span>📝 {inv.invoice_number || 'Change Order'}</span>
                    <span style={s.docRowMeta}>{fmt$(getInvoiceTotal(inv))} · {inv.status}</span>
                  </div>
                ))}
              </div>
            )}
            <button style={s.docTabLink} onClick={() => setTab('invoices')}>View Invoices tab →</button>
          </div>
        </div>
      )}

      {/* ── Photos & Notes tab ── */}
      {tab === 'photos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <h3 style={s.sectionTitle}>Photos</h3>
              <button style={s.uploadBtn} onClick={() => fileRef.current.click()} disabled={uploading}>
                {uploading ? 'Uploading...' : '📷 Add Photo'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={uploadPhoto} style={{ display: 'none' }} />
            </div>
            <p style={{ fontSize: '12px', color: n500, marginBottom: '16px' }}>🔒 Internal photos — never shown to clients</p>
            {photos.length === 0 ? <p style={s.noData}>No photos yet.</p> : (
              <div style={s.photoGrid}>
                {photos.map(p => (
                  <a key={p.id} href={p.url} target="_blank" rel="noreferrer">
                    <img src={p.url} alt="Job" style={s.photo} />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div style={s.section}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '20px' : '24px' }}>
              <div>
                <h3 style={s.sectionTitle}>🔒 Internal Notes</h3>
                <p style={{ fontSize: '12px', color: n500, marginBottom: '12px' }}>Crew instructions and internal info. Never shown to clients.</p>
                <div style={s.notesHistory}>
                  {job.notes ? job.notes.split('\n\n').map((n, i) => (
                    <div key={i} style={s.noteEntry}>{n}</div>
                  )) : <p style={s.noData}>No internal notes yet.</p>}
                </div>
                <div style={s.addNoteRow}>
                  <textarea style={s.noteInput} placeholder="Add crew instruction or internal note..." value={note} onChange={e => setNote(e.target.value)} rows={3} />
                  <button style={s.addNoteBtn} onClick={saveNote}>Save Note</button>
                </div>
              </div>
              <div>
                <h3 style={s.sectionTitle}>📢 Client Updates</h3>
                <p style={{ fontSize: '12px', color: n500, marginBottom: '12px' }}>Professional updates visible to client in their portal.</p>
                <div style={{ background: g50, border: `1px solid ${g200}`, borderRadius: '8px', padding: '14px 16px', fontSize: '13px', color: g600 }}>
                  Client update functionality coming with portal build.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit modal ── */}
      {showEdit && isAdmin && (
        <div style={s.overlay}>
          <div style={s.editModal}>
            <div style={s.editModalHeader}>
              <h2 style={s.editModalTitle}>Edit Job</h2>
              <button style={s.closeBtn} onClick={() => setShowEdit(false)}>✕</button>
            </div>
            <div style={s.editModalBody}>
              <EditSection title="Basic Info">
                <div style={s.editGrid}>
                  <div style={s.editFieldFull}>
                    <label style={s.label}>Job Title</label>
                    <input style={s.editInput} value={editForm.title} onChange={e => ef('title', e.target.value)} />
                  </div>
                  <div>
                    <label style={s.label}>Client</label>
                    <select style={s.editInput} value={editForm.client_id} onChange={e => ef('client_id', e.target.value)}>
                      <option value="">No client</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Job Type</label>
                    <select style={s.editInput} value={editForm.job_type} onChange={e => ef('job_type', e.target.value)}>
                      <option value="">Select...</option>
                      {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Status</label>
                    <select style={s.editInput} value={editForm.status} onChange={e => ef('status', e.target.value)}>
                      {STATUS_OPTIONS.map(st => <option key={st} value={st}>{cap(st)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Priority</label>
                    <select style={s.editInput} value={editForm.priority} onChange={e => ef('priority', e.target.value)}>
                      {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{cap(p)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Internal Job</label>
                    <select style={s.editInput} value={editForm.is_internal ? 'yes' : 'no'} onChange={e => ef('is_internal', e.target.value === 'yes')}>
                      <option value="no">No — Client Job</option>
                      <option value="yes">Yes — Internal</option>
                    </select>
                  </div>
                </div>
              </EditSection>

              <EditSection title="Scheduling">
                <div style={s.editGrid}>
                  <div><label style={s.label}>Start Date</label><input style={s.editInput} type="date" value={editForm.scheduled_date} onChange={e => ef('scheduled_date', e.target.value)} /></div>
                  <div><label style={s.label}>End Date</label><input style={s.editInput} type="date" value={editForm.end_date} onChange={e => ef('end_date', e.target.value)} /></div>
                  <div><label style={s.label}>Start Time</label><input style={s.editInput} type="time" value={editForm.start_time} onChange={e => ef('start_time', e.target.value)} /></div>
                  <div><label style={s.label}>Estimated Hours</label><input style={s.editInput} type="number" step="0.5" value={editForm.estimated_hours} onChange={e => ef('estimated_hours', e.target.value)} /></div>
                  <div><label style={s.label}>Crew Size</label><input style={s.editInput} type="number" value={editForm.crew_size} onChange={e => ef('crew_size', e.target.value)} /></div>
                </div>
              </EditSection>

              <EditSection title="Payment Terms">
                <div style={s.editGrid}>
                  <div>
                    <label style={s.label}>Contract Amount ($)</label>
                    <input style={s.editInput} type="number" step="0.01" value={editForm.contract_amount} onChange={e => ef('contract_amount', e.target.value)} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={s.label}>Deposit Amount ($)</label>
                    <input style={s.editInput} type="number" step="0.01" value={editForm.deposit_amount} onChange={e => ef('deposit_amount', e.target.value)} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={s.label}>Deposit Received</label>
                    <select style={s.editInput} value={editForm.deposit_received ? 'yes' : 'no'} onChange={e => ef('deposit_received', e.target.value === 'yes')}>
                      <option value="no">No — Pending</option>
                      <option value="yes">Yes — Received</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Payment Method</label>
                    <select style={s.editInput} value={editForm.payment_method} onChange={e => ef('payment_method', e.target.value)}>
                      <option value="">Select...</option>
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{cap(m)}</option>)}
                    </select>
                  </div>
                  {/* Payment status is derived — shown read-only */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={s.label}>Payment Status</label>
                    <div style={{ padding: '9px 12px', borderRadius: '8px', border: `1.5px solid ${n200}`, fontSize: '14px', background: n50, color: n500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ textTransform: 'capitalize' }}>{jobFin.paymentStatus || 'unpaid'}</span>
                      <span style={{ fontSize: '11px', fontStyle: 'italic' }}>— Automatically derived from payments. Edit via Payments tab.</span>
                    </div>
                  </div>
                </div>
              </EditSection>

              <EditSection title="Recurring Schedule">
                <div style={s.editGrid}>
                  <div>
                    <label style={s.label}>Recurring Job?</label>
                    <select style={s.editInput} value={editForm.is_recurring ? 'yes' : 'no'} onChange={e => ef('is_recurring', e.target.value === 'yes')}>
                      <option value="no">No — One-time</option>
                      <option value="yes">Yes — Recurring</option>
                    </select>
                  </div>
                  {editForm.is_recurring && (
                    <div>
                      <label style={s.label}>Frequency</label>
                      <select style={s.editInput} value={editForm.recurrence_frequency} onChange={e => ef('recurrence_frequency', e.target.value)}>
                        <option value="">Select...</option>
                        {RECURRENCE_OPTS.map(r => <option key={r} value={r}>{cap(r)}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </EditSection>

              <EditSection title="Location & Details">
                <div style={{ marginBottom: '12px' }}>
                  <label style={s.label}>Address</label>
                  <input style={s.editInput} value={editForm.address} onChange={e => ef('address', e.target.value)} placeholder="Property address" />
                </div>
                <div>
                  <label style={s.label}>Description / Scope</label>
                  <textarea style={{ ...s.editInput, minHeight: '70px', resize: 'vertical' }} value={editForm.description} onChange={e => ef('description', e.target.value)} />
                </div>
              </EditSection>
            </div>
            <div style={s.editModalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowEdit(false)}>Cancel</button>
              <button style={s.saveEditBtn} onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Record Payment modal ── */}
      {showRecordPayment && (
        <div style={s.overlay}>
          <div style={{ ...s.editModal, maxWidth: '420px' }}>
            <div style={s.editModalHeader}>
              <h2 style={s.editModalTitle}>{showRecordPayment._unapplied ? 'Record Unapplied Payment' : 'Record Payment'}</h2>
              <button style={s.closeBtn} onClick={() => setShowRecordPayment(null)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {showRecordPayment._unapplied ? (
                <div style={{ background: au100, border: `1px solid #edd890`, borderRadius: '8px', padding: '12px', fontSize: '13px', color: au600 }}>
                  ⚠️ This payment will be recorded against the job but not applied to any invoice until reconciled. It will appear in Unapplied Payments.
                </div>
              ) : (
                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#15803d' }}>
                  {showRecordPayment.invoice_number} · Total: {fmt$(getInvoiceTotal(showRecordPayment))} ·
                  Previously collected: {fmt$(invFin[showRecordPayment.id]?.appliedTotal || 0)}
                </div>
              )}
              <div>
                <label style={s.label}>Amount Collected ($) *</label>
                <input style={s.editInput} type="number" step="0.01" placeholder="0.00"
                  value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Date *</label>
                <input style={s.editInput} type="date"
                  value={paymentForm.payment_date} onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Method *</label>
                <select style={s.editInput} value={paymentForm.method} onChange={e => setPaymentForm(f => ({ ...f, method: e.target.value }))}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{cap(m)}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Reference # (check number, transaction ID)</label>
                <input style={s.editInput} placeholder="Optional" value={paymentForm.reference}
                  onChange={e => setPaymentForm(f => ({ ...f, reference: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Notes</label>
                <input style={s.editInput} placeholder="Optional" value={paymentForm.notes}
                  onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div style={s.editModalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowRecordPayment(null)}>Cancel</button>
              <button style={s.saveEditBtn} onClick={savePayment} disabled={paymentSaving || !paymentForm.amount}>
                {paymentSaving ? 'Saving...' : `Record ${paymentForm.amount ? fmt$(parseFloat(paymentForm.amount) || 0) : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Milestone modal ── */}
      {showAddMilestone && (
        <div style={s.overlay}>
          <div style={{ ...s.editModal, maxWidth: '400px' }}>
            <div style={s.editModalHeader}>
              <h2 style={s.editModalTitle}>Add Payment Milestone</h2>
              <button style={s.closeBtn} onClick={() => setShowAddMilestone(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={s.label}>Label *</label>
                <input style={s.editInput} placeholder="e.g. 50% Deposit, Final Payment..."
                  value={milestoneForm.label} onChange={e => setMilestoneForm(f => ({ ...f, label: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Amount ($) *</label>
                <input style={s.editInput} type="number" step="0.01" placeholder="0.00"
                  value={milestoneForm.amount} onChange={e => setMilestoneForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Due Date (optional)</label>
                <input style={s.editInput} type="date"
                  value={milestoneForm.due_date} onChange={e => setMilestoneForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div style={s.editModalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowAddMilestone(false)}>Cancel</button>
              <button style={s.saveEditBtn} onClick={saveMilestone} disabled={milestoneSaving || !milestoneForm.label || !milestoneForm.amount}>
                {milestoneSaving ? 'Saving...' : 'Add Milestone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Apply to Invoice modal ── */}
      {showApplyPayment && (
        <div style={s.overlay}>
          <div style={{ ...s.editModal, maxWidth: '400px' }}>
            <div style={s.editModalHeader}>
              <h2 style={s.editModalTitle}>Apply Payment to Invoice</h2>
              <button style={s.closeBtn} onClick={() => { setShowApplyPayment(null); setApplyTargetInvoiceId('') }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ background: au100, border: `1px solid #edd890`, borderRadius: '8px', padding: '12px', fontSize: '13px', color: au600 }}>
                Applying {fmt$(showApplyPayment.amount)} — this will not change the total collected, only link the payment to an invoice.
              </div>
              <div>
                <label style={s.label}>Select Invoice *</label>
                <select style={s.editInput} value={applyTargetInvoiceId} onChange={e => setApplyTargetInvoiceId(e.target.value)}>
                  <option value="">Select an invoice...</option>
                  {invoices
                    .filter(i => i.status !== 'void' && i.status !== 'cancelled')
                    .map(i => {
                      const fi = invFin[i.id] || { balance: 0, invoiceTotal: 0 }
                      return (
                        <option key={i.id} value={i.id}>
                          {i.invoice_number} — {fmt$(fi.invoiceTotal)} ({fmt$(fi.balance)} remaining)
                        </option>
                      )
                    })
                  }
                </select>
              </div>
            </div>
            <div style={s.editModalFooter}>
              <button style={s.cancelBtn} onClick={() => { setShowApplyPayment(null); setApplyTargetInvoiceId('') }}>Cancel</button>
              <button style={s.saveEditBtn} disabled={!applyTargetInvoiceId || applyingSaving}
                onClick={() => applyPaymentToInvoice(showApplyPayment.id, applyTargetInvoiceId)}>
                {applyingSaving ? 'Applying...' : 'Apply Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Time Entry modal ── */}
      {showAddTimeModal && (() => {
        const ciDate = newTime.clock_in_date
        const ciTime = newTime.clock_in_time
        const coTime = newTime.clock_out_time
        const hrs = (ciDate && ciTime && coTime)
          ? (() => { const d = (new Date(`${ciDate}T${coTime}`) - new Date(`${ciDate}T${ciTime}`)) / 3600000; return d > 0 ? d.toFixed(2) : null })()
          : null
        return (
          <div style={s.overlay}>
            <div style={{ ...s.editModal, maxWidth: '420px' }}>
              <div style={s.editModalHeader}>
                <h2 style={s.editModalTitle}>Add Time Entry</h2>
                <button style={s.closeBtn} onClick={() => { setShowAddTimeModal(false); setTimeError('') }}>✕</button>
              </div>
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={s.label}>Employee *</label>
                  <select style={s.editInput} value={newTime.employee_id}
                    onChange={e => setNewTime(t => ({ ...t, employee_id: e.target.value }))}>
                    <option value="">Select employee...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Date *</label>
                  <input style={s.editInput} type="date" value={newTime.clock_in_date}
                    onChange={e => setNewTime(t => ({ ...t, clock_in_date: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={s.label}>Clock In *</label>
                    <input style={s.editInput} type="time" value={newTime.clock_in_time}
                      onChange={e => setNewTime(t => ({ ...t, clock_in_time: e.target.value }))} />
                  </div>
                  <div>
                    <label style={s.label}>Clock Out *</label>
                    <input style={s.editInput} type="time" value={newTime.clock_out_time}
                      onChange={e => setNewTime(t => ({ ...t, clock_out_time: e.target.value }))} />
                  </div>
                </div>
                {hrs && (
                  <div style={{ background: n50, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: n700 }}>
                    Duration: <strong>{hrs}h</strong>
                  </div>
                )}
                {timeError && <div style={s.errorBox}>{timeError}</div>}
              </div>
              <div style={s.editModalFooter}>
                <button style={s.cancelBtn} onClick={() => { setShowAddTimeModal(false); setTimeError('') }}>Cancel</button>
                <button style={s.saveEditBtn} onClick={addTime}
                  disabled={addingTime || !newTime.employee_id || !newTime.clock_in_date || !newTime.clock_in_time || !newTime.clock_out_time}>
                  {addingTime ? 'Saving...' : 'Add Entry'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── Change Order modal ── */}
      {showChangeOrder && (
        <div style={s.overlay}>
          <div style={{ ...s.editModal, maxWidth: '440px' }}>
            <div style={s.editModalHeader}>
              <h2 style={s.editModalTitle}>Add Change Order</h2>
              <button style={s.closeBtn} onClick={() => setShowChangeOrder(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7c6b' }}>
                A change order creates a new draft invoice linked to this job and adjusts the total contract value.
              </p>
              <div>
                <label style={s.label}>Description *</label>
                <input style={s.editInput} placeholder="e.g. Additional tree removal — back yard" value={changeOrder.description} onChange={e => setChangeOrder(c => ({ ...c, description: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Amount ($) *</label>
                <input style={s.editInput} type="number" step="0.01" placeholder="0.00" value={changeOrder.amount} onChange={e => setChangeOrder(c => ({ ...c, amount: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Notes (optional)</label>
                <textarea style={{ ...s.editInput, minHeight: '60px', resize: 'vertical' }} placeholder="Reason for change order, scope details..." value={changeOrder.notes} onChange={e => setChangeOrder(c => ({ ...c, notes: e.target.value }))} />
              </div>
            </div>
            <div style={s.editModalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowChangeOrder(false)}>Cancel</button>
              <button style={s.saveEditBtn} onClick={saveChangeOrder} disabled={coSaving || !changeOrder.description || !changeOrder.amount}>
                {coSaving ? 'Creating...' : 'Create Change Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── NewJob ───────────────────────────────────────────────────────────────────
export function NewJob() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAdmin, profile } = useAuth()
  const [clients, setClients]     = useState([])
  const [employees, setEmployees] = useState([])
  const [estimates, setEstimates] = useState([])
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState('')
  const [showAddClient, setShowAddClient] = useState(false)
  const [form, setForm]           = useState({
    title: '', client_id: searchParams.get('client') || '',
    job_type: '', status: 'pending', priority: 'normal',
    scheduled_date: searchParams.get('date') || '', end_date: '', start_time: searchParams.get('time') || '',
    address: '', description: searchParams.get('scope_text') ? decodeURIComponent(searchParams.get('scope_text')) : '',
    contract_amount: searchParams.get('estimate_price') || '',
    notes: searchParams.get('notes') ? decodeURIComponent(searchParams.get('notes')) : '',
    crew_size: '', deposit_amount: '', deposit_received: false,
    payment_status: 'unpaid', payment_method: '',
    estimate_id: searchParams.get('estimate') || '',
    proposal_id: searchParams.get('proposal') || '',
    estimated_hours: searchParams.get('planned_labor_hours') || '',
    is_internal: false,
    planned_material_cost:  searchParams.get('planned_material_cost') || '',
    planned_labor_cost:     searchParams.get('planned_labor_cost') || '',
    planned_labor_hours:    searchParams.get('planned_labor_hours') || '',
    planned_overhead_cost:  searchParams.get('planned_overhead_cost') || '',
    planned_total_cost:     searchParams.get('planned_total_cost') || '',
    planned_revenue:        searchParams.get('planned_revenue') || searchParams.get('estimate_price') || '',
    planned_gross_profit:   searchParams.get('planned_gross_profit') || '',
    planned_margin:         searchParams.get('planned_margin') || '',
    scope_text: searchParams.get('scope_text') ? decodeURIComponent(searchParams.get('scope_text')) : '',
  })

  useEffect(() => {
    Promise.all([
      supabase.from('clients').select('id, name, address').order('name'),
      supabase.from('profiles').select('id, full_name').eq('active', true).order('full_name'),
      supabase.from('estimates').select('id, job_type, suggested_price, client_id, description').order('created_at', { ascending: false }),
    ]).then(([cl, em, es]) => {
      setClients(cl.data || [])
      setEmployees(em.data || [])
      setEstimates(es.data || [])
    })
  }, [])

  // Auto-fill from estimate
  useEffect(() => {
    if (form.estimate_id && estimates.length > 0) {
      const est = estimates.find(e => e.id === form.estimate_id)
      if (est) setForm(f => ({
        ...f,
        job_type: est.job_type || f.job_type,
        contract_amount: est.suggested_price ? String(est.suggested_price) : f.contract_amount,
        client_id: est.client_id || f.client_id,
        description: est.description || f.description,
      }))
    }
  }, [form.estimate_id, estimates])

  // Auto-fill address from client
  useEffect(() => {
    if (form.client_id && clients.length > 0) {
      const client = clients.find(c => c.id === form.client_id)
      if (client?.address && !form.address) setForm(f => ({ ...f, address: client.address }))
    }
  }, [form.client_id, clients])

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))
  const toggleEmployee = (empId) => setSelectedEmployees(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId])
  const selectedClient = clients.find(c => c.id === form.client_id)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.title.trim()) { setError('Job title is required.'); return }
    setSaving(true)
    const payload = {
      title: form.title, client_id: form.client_id || null,
      job_type: form.job_type || null, status: form.status, priority: form.priority,
      scheduled_date: form.scheduled_date || null, end_date: form.end_date || null,
      start_time: form.start_time || null, address: form.address || null,
      description: form.description || null, notes: form.notes || null,
      contract_amount: form.contract_amount ? parseFloat(form.contract_amount) : null,
      crew_size: form.crew_size ? parseInt(form.crew_size) : null,
      deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
      deposit_received: form.deposit_received, payment_status: form.payment_status,
      payment_method: form.payment_method || null,
      estimate_id: form.estimate_id || null,
      proposal_id: form.proposal_id || null,
      site_visit_id: searchParams.get('site_visit') || null,
      estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      scope_text: form.scope_text || form.description || null,
      is_internal: form.is_internal || false,
      planned_material_cost:  form.planned_material_cost  ? parseFloat(form.planned_material_cost) : null,
      planned_labor_cost:     form.planned_labor_cost     ? parseFloat(form.planned_labor_cost) : null,
      planned_labor_hours:    form.planned_labor_hours    ? parseFloat(form.planned_labor_hours) : null,
      planned_overhead_cost:  form.planned_overhead_cost  ? parseFloat(form.planned_overhead_cost) : null,
      planned_total_cost:     form.planned_total_cost     ? parseFloat(form.planned_total_cost) : null,
      planned_revenue:        form.planned_revenue        ? parseFloat(form.planned_revenue) : (form.contract_amount ? parseFloat(form.contract_amount) : null),
      planned_gross_profit:   form.planned_gross_profit   ? parseFloat(form.planned_gross_profit) : null,
      planned_margin:         form.planned_margin         ? parseFloat(form.planned_margin) : null,
      created_by: profile?.id
    }
    const { data, error: jobError } = await supabase.from('jobs').insert([payload]).select().single()
    if (jobError) { setError(jobError.message); setSaving(false); return }

    const siteVisitId = searchParams.get('site_visit')
    if (siteVisitId) {
      // .eq('status', 'Open') guards against a resubmit re-pointing an
      // already-converted site visit at a duplicate job.
      const { error: svErr } = await supabase.from('site_visits').update({
        status: 'Converted', converted_to_job_id: data.id,
      }).eq('id', siteVisitId).eq('status', 'Open')
      if (svErr) setError('Job saved, but could not mark the source site visit as converted: ' + svErr.message)
    }

    // Parallel: assign employees + create draft invoice + mark estimate approved
    const ops = []
    if (selectedEmployees.length > 0) {
      ops.push(supabase.from('job_assignments').insert(selectedEmployees.map(emp_id => ({ job_id: data.id, employee_id: emp_id }))))
    }
    if (form.estimate_id) {
      ops.push(supabase.from('estimates').update({ status: 'approved' }).eq('id', form.estimate_id))
    }
    const dueDate = new Date(); dueDate.setDate(dueDate.getDate() + 30)
    ops.push(supabase.from('invoices').insert([{
      job_id: data.id, client_id: payload.client_id,
      invoice_number: `INV-${Date.now().toString().slice(-6)}`,
      title: `Invoice for ${form.title}`, amount: payload.contract_amount,
      total: payload.contract_amount, due_date: dueDate.toISOString().split('T')[0],
      status: 'draft', invoice_type: 'final', notes: 'Thank you for your business!',
    }]))
    await Promise.all(ops)
    navigate(`/jobs/${data.id}`)
  }

  return (
    <div style={s.page}>
      <button onClick={() => navigate(-1)} style={s.back}>← Back</button>
      <h1 style={s.title}>New Job</h1>
      <form onSubmit={handleSubmit} style={s.form}>
        {error && <div style={s.errorBox}>{error}</div>}

        <FormSection title="Basic Info">
          <div style={s.formGrid}>
            <div style={s.fieldFull}>
              <label style={s.label}>Job Title *</label>
              <input style={s.input} value={form.title} onChange={e => set('title', e.target.value)} required placeholder="e.g. Tree Trimming — Smith Property" />
            </div>
            <div>
              <label style={s.label}>Client</label>
              <select
                style={s.input}
                value={form.client_id}
                onChange={e => {
                  if (e.target.value === '__add_new__') { setShowAddClient(true); return }
                  set('client_id', e.target.value)
                }}
              >
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="__add_new__">+ Add New Client</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Job Type</label>
              <select style={s.input} value={form.job_type} onChange={e => set('job_type', e.target.value)}>
                <option value="">Select type...</option>
                {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Status</label>
              <select style={s.input} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUS_OPTIONS.map(st => <option key={st} value={st}>{cap(st)}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Priority</label>
              <select style={s.input} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{cap(p)}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Linked Estimate</label>
              <select style={s.input} value={form.estimate_id} onChange={e => set('estimate_id', e.target.value)}>
                <option value="">None</option>
                {estimates.map(e => <option key={e.id} value={e.id}>{e.job_type || 'Estimate'} — {fmt$(e.suggested_price || 0)}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Internal Job</label>
              <select style={s.input} value={form.is_internal ? 'yes' : 'no'} onChange={e => set('is_internal', e.target.value === 'yes')}>
                <option value="no">No — Client Job</option>
                <option value="yes">Yes — Internal / Shop</option>
              </select>
            </div>
          </div>
        </FormSection>

        <FormSection title="Scheduling">
          <div style={s.formGrid}>
            <div><label style={s.label}>Start Date</label><input style={s.input} type="date" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} /></div>
            <div><label style={s.label}>End Date</label><input style={s.input} type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} /></div>
            <div><label style={s.label}>Start Time</label><input style={s.input} type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} /></div>
            <div><label style={s.label}>Estimated Hours</label><input style={s.input} type="number" step="0.5" value={form.estimated_hours} onChange={e => set('estimated_hours', e.target.value)} placeholder="0" /></div>
          </div>
        </FormSection>

        <FormSection title="Crew">
          <div style={s.formGrid}>
            <div><label style={s.label}>Crew Size Needed</label><input style={s.input} type="number" value={form.crew_size} onChange={e => set('crew_size', e.target.value)} placeholder="0" /></div>
          </div>
          <label style={s.label}>Assign Employees</label>
          <div style={s.employeeCheckboxes}>
            {employees.map(emp => (
              <label key={emp.id} style={s.checkboxLabel}>
                <input type="checkbox" checked={selectedEmployees.includes(emp.id)} onChange={() => toggleEmployee(emp.id)} style={{ marginRight: '8px' }} />
                {emp.full_name}
              </label>
            ))}
          </div>
        </FormSection>

        {isAdmin && (
          <FormSection title="Payment Terms">
            <div style={s.formGrid}>
              <div><label style={s.label}>Contract Amount ($)</label><input style={s.input} type="number" step="0.01" value={form.contract_amount} onChange={e => set('contract_amount', e.target.value)} placeholder="0.00" /></div>
              <div><label style={s.label}>Deposit Amount ($)</label><input style={s.input} type="number" step="0.01" value={form.deposit_amount} onChange={e => set('deposit_amount', e.target.value)} placeholder="0.00" /></div>
              <div>
                <label style={s.label}>Deposit Received</label>
                <select style={s.input} value={form.deposit_received ? 'yes' : 'no'} onChange={e => set('deposit_received', e.target.value === 'yes')}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              <div>
                <label style={s.label}>Payment Method</label>
                <select style={s.input} value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
                  <option value="">Select...</option>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{cap(m)}</option>)}
                </select>
              </div>
              {/* Payment status is derived — not manually set. Initialized to 'unpaid' on submit. */}
            </div>
          </FormSection>
        )}

        <FormSection title="Location & Details">
          <div style={{ marginBottom: '16px' }}>
            <label style={s.label}>
              Job Address {selectedClient?.address && <span style={{ color: '#16a34a', fontSize: '11px' }}>✓ Auto-filled</span>}
            </label>
            <input style={s.input} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Property address" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={s.label}>Description / Scope of Work</label>
            <textarea style={{ ...s.input, minHeight: '80px', resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the work..." />
          </div>
          <div>
            <label style={s.label}>Notes / Special Instructions</label>
            <textarea style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any special instructions..." />
          </div>
        </FormSection>

        <button style={s.submitBtn} type="submit" disabled={saving}>
          {saving ? 'Creating Job...' : 'Create Job'}
        </button>
      </form>

      {showAddClient && (
        <QuickAddClientModal
          onClose={() => setShowAddClient(false)}
          onCreated={(newClient) => {
            setClients(cs => [...cs, newClient].sort((a, b) => a.name.localeCompare(b.name)))
            set('client_id', newClient.id)
            setShowAddClient(false)
          }}
        />
      )}
    </div>
  )
}

// ─── Small reusable components ────────────────────────────────────────────────
function FinRow({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '14px' }}>
      <span style={{ color: '#6b7c6b', fontWeight: bold ? '700' : '400' }}>{label}</span>
      <span style={{ fontWeight: bold ? '700' : '600', color: color || '#1a2e1a' }}>{value}</span>
    </div>
  )
}

function Detail({ label, value, children }) {
  const content = children || (value ? <div style={detailStyles.value}>{value}</div> : null)
  if (!content) return null
  return (
    <div style={detailStyles.box}>
      <div style={detailStyles.label}>{label}</div>
      {children ? <div style={detailStyles.value}>{children}</div> : <div style={detailStyles.value}>{value}</div>}
    </div>
  )
}

function EditSection({ title, children }) {
  return (
    <div style={s.editSection}>
      <div style={s.editSectionTitle}>{title}</div>
      {children}
    </div>
  )
}

function FormSection({ title, children }) {
  return (
    <div style={s.formSection}>
      <h3 style={s.formSectionTitle}>{title}</h3>
      {children}
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page:    { width: '100%' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title:   { margin: '0 0 4px', fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub:     { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  addBtn:  { background: '#2d4a2d', color: 'white', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' },
  filterToggleBtn: { background: 'white', border: '1.5px solid #d1d5db', color: '#374151', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  search:  { width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', marginBottom: '12px', boxSizing: 'border-box', fontFamily: 'inherit' },
  filters: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  filterBtn:    { padding: '6px 14px', borderRadius: '20px', border: '1.5px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151', fontFamily: 'inherit' },
  filterActive: { background: '#2d4a2d', color: 'white', borderColor: '#2d4a2d' },
  advFilters:   { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' },
  advSelect:    { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', background: 'white' },
  clearFiltersBtn: { padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', background: 'white', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', color: '#6b7c6b' },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  list:    { display: 'flex', flexDirection: 'column', gap: '8px' },
  card:    { background: 'white', borderRadius: '10px', padding: '16px', textDecoration: 'none', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'block' },
  cardInternal: { background: '#f9fafb', borderColor: '#d1d5db' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  jobTitle: { fontWeight: '600', color: '#1a2e1a', fontSize: '16px' },
  statusBadge:   { color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' },
  priorityBadge: { color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' },
  internalBadge: { background: '#f3f4f6', color: '#6b7280', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  cardBottom: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  meta:       { fontSize: '13px', color: '#6b7c6b' },
  empty:      { textAlign: 'center', padding: '40px 0', color: '#6b7c6b' },
  back:       { background: 'none', border: 'none', color: '#2d4a2d', cursor: 'pointer', fontSize: '14px', fontWeight: '600', padding: '0 0 16px', display: 'block' },

  // Job detail header
  jobHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '16px' },
  jobHeaderLeft: { flex: 1 },
  jobMeta:       { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' },
  clientLink:    { fontSize: '13px', color: '#2d4a2d', fontWeight: '600' },
  metaTag:       { fontSize: '13px', color: '#6b7c6b' },
  statusSelect:  { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  editJobBtn:    { background: '#1e3a5f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' },
  deleteJobBtn:  { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' },
  templateBtn:   { background: 'white', border: '1.5px solid #2d4a2d', color: '#2d4a2d', padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  milestoneRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f9fafb', borderRadius: '8px', gap: '12px', flexWrap: 'wrap' },
  milestoneLeft: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1 },
  msBadge:       { color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', flexShrink: 0 },

  // Metrics bar
  metricsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginBottom: '14px' },
  metricCard:  { background: 'white', borderRadius: '10px', padding: '14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  metricValue: { fontSize: '17px', fontWeight: '700', color: '#1a2e1a' },
  metricLabel: { fontSize: '11px', color: '#6b7c6b', textTransform: 'uppercase', marginTop: '4px' },
  metricSub:   { fontSize: '11px', color: '#9ca3af', marginTop: '2px' },

  // Payment panel
  paymentPanel:  { background: 'white', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  paymentRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' },
  paymentLabel:  { fontSize: '13px', color: '#6b7c6b' },
  paymentValue:  { fontSize: '14px', fontWeight: '600', color: '#1a2e1a' },
  paymentSelect: { padding: '7px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', cursor: 'pointer' },
  changeOrderBtn: { background: '#d97706', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },

  // Tabs
  tabs:      { display: 'flex', gap: '4px', marginBottom: '20px', flexWrap: 'wrap' },
  tab:       { padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151', fontFamily: 'inherit' },
  tabActive: { background: '#2d4a2d', color: 'white', borderColor: '#2d4a2d' },

  // Section
  section:       { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  sectionTitle:  { margin: '0 0 16px', fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  detailGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' },
  descBox:       { background: '#f9fafb', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#374151', marginBottom: '12px' },
  estimateLink:  { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#374151' },
  noData:        { color: '#6b7c6b', fontSize: '14px' },
  warnBox:       { background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px 16px', fontSize: '13px', color: '#92400e' },
  errorBox:      { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' },
  docList:       { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' },
  docRow:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', background: '#f9fafb', borderRadius: '8px', fontSize: '13.5px', color: '#1a2e1a', textDecoration: 'none', gap: '10px' },
  docRowMeta:    { fontSize: '12.5px', color: '#6b7c6b', whiteSpace: 'nowrap' },
  docTabLink:    { background: 'none', border: 'none', color: '#2d6a2d', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },

  // Crew
  crewList:      { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  crewTag:       { display: 'flex', alignItems: 'center', gap: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '8px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
  addCrewBtn:    { background: '#2d4a2d', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  removeCrewBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px', padding: '0 2px', fontFamily: 'inherit' },

  // Invoices
  invoiceRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f9fafb', borderRadius: '8px', gap: '12px', flexWrap: 'wrap' },
  invoiceLeft:      { display: 'flex', alignItems: 'center', gap: '10px', flex: 1 },
  invoiceRight:     { display: 'flex', alignItems: 'center', gap: '8px' },
  invoiceTypeBadge: { color: 'white', padding: '3px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  deleteSmallBtn:   { background: 'none', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', fontFamily: 'inherit' },

  // Costs
  addCostForm: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px', padding: '16px', background: '#f9fafb', borderRadius: '8px' },
  select:      { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  costInput:   { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', flex: 1, minWidth: '120px', boxSizing: 'border-box' },
  addCostBtn:  { background: '#2d4a2d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' },
  costList:    { display: 'flex', flexDirection: 'column', gap: '8px' },
  costRow:     { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px' },
  costCat:     { color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize', flexShrink: 0 },
  costDesc:    { flex: 1, fontSize: '14px', color: '#374151' },
  costAmt:     { fontWeight: '700', color: '#1a2e1a', fontSize: '15px' },
  deleteCostBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px', padding: '0 4px' },
  totalRow:    { borderTop: '2px solid #e5e7eb', paddingTop: '12px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' },

  // Time
  timeList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  timeRow:  { display: 'grid', gridTemplateColumns: '1fr 1.5fr 1.5fr 60px 80px', gap: '8px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px', alignItems: 'center' },
  timeName:  { fontWeight: '600', color: '#1a2e1a' },
  timeClock: { color: '#6b7c6b' },
  timeHours: { fontWeight: '700', color: '#2d4a2d', textAlign: 'right' },
  // Mobile: the 5-column row above doesn't fit two full datetimes on a phone width, so this
  // is a genuinely different structure, not just fewer columns — a stacked labeled card.
  timeRowMobile:     { padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px' },
  timeRowMobileTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  timeRowMobileMeta: { color: '#6b7c6b', fontSize: '12px', lineHeight: 1.5 },

  // Photos
  uploadBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' },
  photo:     { width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', display: 'block' },

  // Notes
  notesHistory: { marginBottom: '16px' },
  noteEntry:    { background: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '8px', fontSize: '14px', color: '#374151', whiteSpace: 'pre-line' },
  addNoteRow:   { display: 'flex', flexDirection: 'column', gap: '8px' },
  noteInput:    { padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' },
  addNoteBtn:   { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', alignSelf: 'flex-end', fontFamily: 'inherit' },

  // Edit modal
  overlay:          { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' },
  editModal:        { background: 'white', borderRadius: '16px', width: '100%', maxWidth: '620px', marginTop: '20px', display: 'flex', flexDirection: 'column' },
  editModalHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb' },
  editModalTitle:   { margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a2e1a' },
  closeBtn:         { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7c6b', padding: '4px' },
  editModalBody:    { padding: '20px 24px', overflowY: 'auto', maxHeight: '70vh' },
  editModalFooter:  { display: 'flex', gap: '10px', padding: '16px 24px', borderTop: '1px solid #e5e7eb' },
  editSection:      { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #f3f4f6' },
  editSectionTitle: { fontSize: '12px', fontWeight: '700', color: '#2d4a2d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  editGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' },
  editFieldFull:    { gridColumn: '1 / -1' },
  editInput:        { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  cancelBtn:        { flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #d1d5db', background: 'white', fontSize: '15px', fontWeight: '600', cursor: 'pointer', color: '#374151', fontFamily: 'inherit' },
  saveEditBtn:      { flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: '#2d4a2d', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },

  // New job form
  form:            { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '0' },
  formSection:     { marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #f3f4f6' },
  formSectionTitle:{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#2d4a2d', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formGrid:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' },
  fieldFull:       { gridColumn: '1 / -1' },
  label:           { display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:           { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  employeeCheckboxes: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' },
  checkboxLabel:      { display: 'flex', alignItems: 'center', background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', fontSize: '14px', cursor: 'pointer', color: '#374151' },
  submitBtn:       { background: '#2d4a2d', color: 'white', border: 'none', padding: '14px 28px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px', fontFamily: 'inherit' },
}

const detailStyles = {
  box:   { background: '#f9fafb', borderRadius: '8px', padding: '12px' },
  label: { fontSize: '11px', color: '#6b7c6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  value: { fontSize: '15px', fontWeight: '600', color: '#1a2e1a' },
}
